import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { trackAccountCreated, trackMetaApiConnected, trackAccountGroupCreated } from '../client/utils/posthog';
import { CURRENCY } from '../shared/currency';
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useQuery,
  getAccountsByUser,
  getAccountGroupsByUser,
  getPropFirmTemplates,
  getAccountChargesByUser,
  getPayoutSuggestions,
  getAccountResetSuggestions,
  createAccounts,
  updateAccount,
  setFundedPredecessor,
  deleteAccount,
  createAccountGroup,
  updateAccountGroup,
  deleteAccountGroup,
  createPayout,
  deletePayout,
  getPayoutsByAccount,
  connectMetaApiAccount,
  getMetaApiConnection,
  getTradovateConnections,
  getTradingStrategiesByUser,
  createTradingStrategy,
  deleteTradingStrategy,
  resyncTradesStrategies,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useToast } from "../client/hooks/use-toast";
import { cn } from "../client/utils";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../client/components/ui/sheet";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Progress } from "../client/components/ui/progress";
import { Separator } from "../client/components/ui/separator";
import useTranslation from "../client/hooks/useTranslation";
import RuleUpdateBanner from "./components/RuleUpdateBanner";
import NoRulesBanner from "./components/NoRulesBanner";
import { resolveConsistencyRule } from "./propFirmEngine";
import { normalizePricingModel, isDirectFunded, isSubscription } from "./pricingModel";
import { editFormPricingModel } from "./editFormPricingModel";
import { normalizeStatusForSubmit, toggleDirectFundedRows, propagateAutoSwitched } from "./wizardRowStatus";
import { AccountCard } from "./components/AccountCard";
import PredecessorSelectField from "./components/PredecessorSelectField";
import { PropFirmBadge, STATUS_STYLES, formatCurrency } from "./accountCardShared";
import { Card, CardContent } from "../client/components/ui/card";
import {
  Plus,
  Building2,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  Power,
  PowerOff,
  FolderOpen,
  Folder,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Sparkles,
  ChevronUp,
  CheckCircle2,
  Info,
  DollarSign,
  Calendar,
  Clock,
  X,
  ExternalLink,
  MoreHorizontal,
  Plug,
  RefreshCw,
  LayoutGrid,
  Table2,
  Pencil,
} from "lucide-react";
import AccountTable from "./AccountTable";
import AccountSourceBadge from "./AccountSourceBadge";
import useLocalStorage from "../client/hooks/useLocalStorage";
import MetaApiConnectModal from "./MetaApiConnectModal";
import { METATRADER_SYNC_CONNECT_ENABLED } from "./metaApiFeatureFlag";
import { normalizePayoutTarget } from "./payoutTarget";
import { deactivationBadge, deactivationLabels } from "./deactivationBadge";
// Conversions en heure LOCALE : toISOString() décale du fuseau et faussait
// fundedAt (voir resetDateInput.ts).
import { toDateInputValue, toDateTimeInputValue } from "./resetDateInput";
import PromoteToFundedModal from "./PromoteToFundedModal";
import StrategyMultiSelect from "../client/components/StrategyMultiSelect";
import MetaApiSyncStatus from "./MetaApiSyncStatus";
import TradovateSyncStatus from "./tradovate/TradovateSyncStatus";
import TradovateConnectWizard from "./tradovate/TradovateConnectWizard";
import TradovateImportReview from "./tradovate/TradovateImportReview";
import { normalizePropFirmTemplateId } from "./tradovate/tradovatePropFirmParam";
import TradovateConnectionsSection from "./tradovate/TradovateConnectionsSection";
import TradovateDiscoveredBanners from "./tradovate/TradovateDiscoveredBanners";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../client/components/ui/dropdown-menu";
import { PaymentPlanId, SubscriptionStatus, userHasBrokerSync } from "../payment/plans";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import AccountChargesManager from "./AccountChargesManager";
import BalanceFreshnessHint from "./BalanceFreshnessHint";
import BalanceGapHint from "./BalanceGapHint";
import { buildViolationDetails } from "./violationLabels";
import { formatViolationLabel } from "./formatViolationLabel";
import { BeamButton } from "../client/components/ui/beam-button";
import { motion } from "framer-motion";
import { PageTransition } from "../client/components/ui/motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";
import type {
  AccountStatus,
  TierSnapshot,
  AccountTypeSnapshot,
  PropFirmTemplateSnapshot,
  ChallengeProgress,
  BufferStatus,
  PayoutProgress,
  EnrichedAccount,
  EnrichedGroup,
} from "./accountTypes";
import { isPropFirmFundedStatus } from "./accountStatus";
import PayoutAmountFields from "./PayoutAmountFields";
import EditPayoutDialog, { type EditablePayout } from "./EditPayoutDialog";
import { EMPTY_PAYOUT_VALUES, resolvePayoutForm, type PayoutFormValues } from "./payoutForm";

// ─── Helpers ──────────────────────────────────────────────────────────────────


// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: AccountStatus }) {
  const t = useTranslation();
  const configs: Record<AccountStatus, { label: string; className: string }> = {
    challenge: { label: t.tradingAccounts.statusChallenge, className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" },
    pa: { label: t.tradingAccounts.statusPa, className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    inactive: { label: t.tradingAccounts.statusInactive, className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" },
    demo: { label: t.tradingAccounts.statusDemo, className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    live: { label: t.tradingAccounts.statusLive, className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  };
  const cfg = configs[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

// ─── PropFirm Selector ────────────────────────────────────────────────────────

function PropFirmSelector({
  value,
  onChange,
  templates,
  placeholder,
  allowNone = true,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  templates: PropFirmTemplateSnapshot[];
  placeholder?: string;
  allowNone?: boolean;
}) {
  const t = useTranslation();
  const systemTemplates = templates.filter((t) => t.isSystem);
  const customTemplates = templates.filter((t) => !t.isSystem);

  const selectValue = value ?? "__none__";

  return (
    <Select
      value={selectValue}
      onValueChange={(v) => onChange(v === "__none__" ? null : v)}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder ?? t.tradingAccounts.selectPropFirm}>
          {value ? (
            (() => {
              const tpl = templates.find((t) => t.id === value);
              return tpl ? (
                <span className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: tpl.color }}
                  />
                  {tpl.name}
                </span>
              ) : null;
            })()
          ) : (
            <span className="text-muted-foreground">{t.tradingAccounts.noPropFirm}</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {allowNone && (
          <SelectItem value="__none__">{t.tradingAccounts.noPropFirm}</SelectItem>
        )}
        {systemTemplates.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs text-muted-foreground font-medium">
              {t.tradingAccounts.systemPropFirms}
            </div>
            {systemTemplates.map((tpl) => (
              <SelectItem key={tpl.id} value={tpl.id}>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tpl.color }} />
                  {tpl.name}
                </span>
              </SelectItem>
            ))}
          </>
        )}
        {customTemplates.length > 0 && (
          <>
            <div className="px-2 py-1 text-xs text-muted-foreground font-medium mt-1">
              {t.tradingAccounts.myCustomPropFirms}
            </div>
            {customTemplates.map((tpl) => (
              <SelectItem key={tpl.id} value={tpl.id}>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tpl.color }} />
                  {tpl.name}
                </span>
              </SelectItem>
            ))}
          </>
        )}
      </SelectContent>
    </Select>
  );
}

// ─── Tier Selector ────────────────────────────────────────────────────────────

function TierSelector({
  template,
  value,
  onChange,
  initialBalance,
}: {
  template: PropFirmTemplateSnapshot;
  value: string | null;
  onChange: (v: string | null) => void;
  initialBalance: number;
}) {
  const t = useTranslation();

  // Flatten all tiers across all account types
  const allTiers = useMemo(
    () => (template.accountTypes ?? []).flatMap((at) => at.tiers),
    [template.accountTypes],
  );

  // Auto-detect tier from initialBalance
  const autoTier = useMemo((): TierSnapshot | null => {
    const sorted = [...allTiers].sort((a, b) => a.minBalance - b.minBalance);
    let matched: TierSnapshot | null = null;
    for (const tier of sorted) {
      if (initialBalance >= tier.minBalance && initialBalance <= tier.maxBalance) {
        matched = tier;
        break;
      }
    }
    if (!matched) {
      for (const tier of sorted) {
        if (initialBalance >= tier.minBalance) matched = tier;
      }
    }
    return matched ?? sorted[0] ?? null;
  }, [allTiers, initialBalance]);

  if (allTiers.length === 0) return null;

  return (
    <div className="space-y-1">
      <Select value={value ?? "__auto__"} onValueChange={(v) => onChange(v === "__auto__" ? null : v)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__auto__">
            <span className="flex items-center gap-2">
              <span className="text-primary text-xs font-medium">{t.tradingAccounts.autoDetected}</span>
              {autoTier && <span className="text-muted-foreground text-xs">({autoTier.label})</span>}
            </span>
          </SelectItem>
          {(template.accountTypes ?? []).map((at) => (
            at.tiers.map((tier) => (
              <SelectItem key={tier.id} value={tier.id}>
                {template.accountTypes.length > 1 ? `${at.name} · ${tier.label}` : tier.label}
              </SelectItem>
            ))
          ))}
        </SelectContent>
      </Select>
      {value && (
        <p className="text-xs text-muted-foreground">{t.tradingAccounts.tierOverride}</p>
      )}
    </div>
  );
}

// ─── Account Card ─────────────────────────────────────────────────────────────

// ─── Account Detail Sheet ─────────────────────────────────────────────────────

function AccountDetailSheet({
  account,
  open,
  onClose,
  onRefetch,
}: {
  account: EnrichedAccount | null;
  open: boolean;
  onClose: () => void;
  onRefetch: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const [addingPayout, setAddingPayout] = useState(false);
  const [payoutValues, setPayoutValues] = useState<PayoutFormValues>(EMPTY_PAYOUT_VALUES);
  const [editingPayout, setEditingPayout] = useState<EditablePayout | null>(null);
  const [payoutDate, setPayoutDate] = useState(toDateInputValue(new Date()));
  const [payoutNotes, setPayoutNotes] = useState("");
  const [payoutLoading, setPayoutLoading] = useState(false);

  const { data: payouts = [], refetch: refetchPayouts } = useQuery(
    getPayoutsByAccount,
    account ? { accountId: account.id } : undefined,
    { enabled: !!account },
  );

  const handleAddPayout = async () => {
    if (!account) return;
    // `amount` = NET reçu, `grossAmount` = BRUT débité (voir payoutForm.ts).
    const resolved = resolvePayoutForm(payoutValues);
    if (!resolved.ok) {
      toast({
        title: resolved.reason === "gross_below_net" ? t.tradingAccounts.payoutGrossBelowNet : t.tradingAccounts.payoutAmountsRequired,
        variant: "destructive",
      });
      return;
    }
    setPayoutLoading(true);
    try {
      await createPayout({
        accountId: account.id,
        date: payoutDate,
        amount: resolved.amount,
        grossAmount: resolved.grossAmount,
        notes: payoutNotes || null,
      });
      toast({ title: t.tradingAccounts.payoutAdded });
      setAddingPayout(false);
      setPayoutValues(EMPTY_PAYOUT_VALUES);
      setPayoutNotes("");
      refetchPayouts();
      onRefetch();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleDeletePayout = async (id: string) => {
    if (!window.confirm(t.tradingAccounts.payoutDeleteConfirm)) return;
    try {
      await deletePayout({ id });
      toast({ title: t.tradingAccounts.payoutDeleted });
      refetchPayouts();
      onRefetch();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    }
  };

  if (!account) return null;

  const tier = account.activeTier;
  const template = account.effectiveTemplate;
  // Règle du statut COURANT (challenge ou funded), résolue comme le moteur.
  const consistencyRule = resolveConsistencyRule(
    tier,
    account.resolvedPhase ?? null,
    account.status,
    account.consistencyRulePercent,
  );
  const pnl = account.currentBalance - account.initialBalance;
  const isPositive = pnl >= 0;
  const buffer = account.bufferStatus;
  const challenge = account.challengeProgress;
  const payout = account.payoutProgress;
  const isChallenge = account.status === "challenge";
  const isFunded = isPropFirmFundedStatus(account.status, !!template);

  const totalPayoutsReceived = (payouts as any[]).reduce((s: number, p: any) => s + p.amount, 0);

  function RuleRow({ label, value }: { label: string; value: string | null }) {
    if (!value) return null;
    return (
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {account.name}
            <StatusBadge status={account.status} />
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">

          {/* Section 1: Key metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground mb-0.5">{t.tradingAccounts.currentBalance}</p>
              <p className="text-base font-bold font-mono">{formatCurrency(account.currentBalance, CURRENCY)}</p>
              <BalanceFreshnessHint
                brokerBalance={(account as any).brokerBalance}
                brokerBalanceAt={(account as any).brokerBalanceAt}
                className="mt-1"
              />
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground mb-0.5">{t.tradingAccounts.variation}</p>
              <p className={`text-base font-bold font-mono ${isPositive ? "text-pnlPositive" : "text-pnlNegative"}`}>
                {isPositive ? "+" : ""}{formatCurrency(pnl, CURRENCY)}
              </p>
            </div>
            <div className="rounded-lg bg-muted/40 p-3">
              <p className="text-xs text-muted-foreground mb-0.5">{t.tradingAccounts.initialBalance}</p>
              <p className="text-sm font-semibold font-mono">{formatCurrency(account.initialBalance, CURRENCY)}</p>
            </div>
            {buffer?.highWaterMark && (
              <div className="rounded-lg bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground mb-0.5">{t.tradingAccounts.peakBalance}</p>
                <p className="text-sm font-semibold font-mono">{formatCurrency(buffer.highWaterMark, CURRENCY)}</p>
              </div>
            )}
          </div>

          {/* Section 2: Prop firm + tier */}
          {template && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{t.tradingAccounts.activeTier}</h3>
                  <PropFirmBadge template={template} />
                </div>
                {tier ? (
                  <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t.tradingAccounts.activeTier}</span>
                      <span className="font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded text-xs">{tier.label}</span>
                    </div>
                    <Separator />
                    <RuleRow label={t.tradingAccounts.profitTarget} value={tier.profitTargetPercent ? `${tier.profitTargetPercent}%` : null} />
                    <RuleRow label={t.tradingAccounts.minTradingDays} value={tier.minTradingDays ? `${tier.minTradingDays} jours` : null} />
                    <RuleRow label={t.tradingAccounts.maxDailyLoss} value={tier.maxDailyLossPercent ? `${tier.maxDailyLossPercent}%` : null} />
                    <RuleRow label={t.tradingAccounts.maxTotalLoss} value={tier.maxTotalLossPercent ? `${tier.maxTotalLossPercent}%` : null} />
                    <RuleRow label={t.tradingAccounts.trailingDrawdown} value={tier.hasTrailingDrawdown && tier.trailingDrawdownPercent ? `${tier.trailingDrawdownPercent}% (trailing)` : null} />
                    <RuleRow label={t.tradingAccounts.consistencyRule} value={consistencyRule !== null ? t.tradingAccounts.consistencyRuleValue.replace("{percent}", String(consistencyRule)) : null} />
                    <RuleRow label={t.tradingAccounts.maxContracts} value={tier.maxContracts ? String(tier.maxContracts) : null} />
                    <RuleRow label={t.tradingAccounts.payoutCooldown} value={tier.payoutCooldownDays ? `${tier.payoutCooldownDays} jours` : null} />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{t.tradingAccounts.noRulesDefined}</p>
                )}
              </div>
            </>
          )}

          {/* Section 3: Challenge progress */}
          {template && isChallenge && challenge && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">{t.tradingAccounts.challengeProgress}</h3>
                {challenge.isComplete && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-950/30 rounded-lg px-3 py-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {t.tradingAccounts.challengeComplete}
                  </div>
                )}
                {(challenge.targetPercent !== null || challenge.targetAmount !== null) && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.tradingAccounts.profitTarget}</span>
                      <span className="font-medium">
                        {challenge.targetAmount !== null
                          ? `${formatCurrency(challenge.profit, CURRENCY)} / ${formatCurrency(challenge.targetAmount, CURRENCY)}`
                          : `${challenge.profitPercent.toFixed(2)}% / ${challenge.targetPercent}%`}
                      </span>
                    </div>
                    <Progress value={Math.round(challenge.ratio * 100)} className="h-2" />
                    {challenge.targetPercent !== null && (
                      <p className="text-xs text-right text-muted-foreground">
                        {formatCurrency(pnl, CURRENCY)} / {formatCurrency((challenge.targetPercent / 100) * account.initialBalance, CURRENCY)}
                      </p>
                    )}
                  </div>
                )}
                {challenge.minTradingDays !== null && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.tradingAccounts.tradingDays}</span>
                      <span className="font-medium">{challenge.daysTraded} / {challenge.minTradingDays} jours</span>
                    </div>
                    <Progress value={Math.round(challenge.daysRatio * 100)} className="h-2" />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Section 4: Buffer + payout (funded) */}
          {template && isFunded && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">{t.tradingAccounts.bufferDrawdown}</h3>
                {buffer && buffer.ruleType ? (
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t.tradingAccounts.bufferRemaining}</span>
                      <span className={`font-medium ${buffer.isViolation ? "text-red-500" : buffer.isWarning ? "text-yellow-500" : "text-green-500"}`}>
                        {buffer.isViolation ? "🚫 " : ""}{formatCurrency(buffer.remainingRoom, CURRENCY)}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          buffer.isViolation ? "bg-red-500" : buffer.isWarning ? "bg-yellow-500" : "bg-primary"
                        }`}
                        style={{ width: `${Math.round(buffer.usedRatio * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{Math.round(buffer.usedRatio * 100)}% {t.tradingAccounts.bufferConsumed}</span>
                      <span>Max: {formatCurrency(buffer.maxAllowedLoss, CURRENCY)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{t.tradingAccounts.noRulesDefined}</p>
                )}

                <h3 className="text-sm font-semibold pt-1">{t.tradingAccounts.payoutProgress}</h3>
                {payout ? (
                  <div className="space-y-1.5">
                    {payout.targetAmount !== null ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t.tradingAccounts.payoutTarget}</span>
                          <span className={`font-medium ${payout.isReached ? "text-green-500" : ""}`}>
                            {formatCurrency(payout.currentProfit, CURRENCY)} / {formatCurrency(payout.targetAmount, CURRENCY)}
                          </span>
                        </div>
                        <Progress value={Math.round(payout.ratio * 100)} className="h-2" />
                        {payout.isReached && (
                          <p className="text-xs text-green-500 font-medium">{t.tradingAccounts.payoutReached}</p>
                        )}
                        {payout.cooldownDays && (
                          <p className="text-xs text-muted-foreground">
                            <Clock className="inline h-3 w-3 mr-1" />
                            {t.tradingAccounts.payoutCooldown}: {payout.cooldownDays} jours
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t.tradingAccounts.setPayoutTarget}</p>
                    )}
                  </div>
                ) : null}
              </div>
            </>
          )}

          {/* Section 5: Payout history */}
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{t.tradingAccounts.payoutsHistory}</h3>
              {totalPayoutsReceived > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t.tradingAccounts.totalPayoutsReceived}: <span className="font-semibold text-foreground">{formatCurrency(totalPayoutsReceived, CURRENCY)}</span>
                </span>
              )}
            </div>

            {(payouts as any[]).length === 0 ? (
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.noPayouts}</p>
            ) : (
              <div className="space-y-2">
                {(payouts as any[]).map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold font-mono text-pnlPositive">+{formatCurrency(p.amount, CURRENCY)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.date).toLocaleDateString()}
                        {p.grossAmount != null && (
                          <span className="font-mono"> · {t.tradingAccounts.payoutGrossShort.replace("{amount}", formatCurrency(p.grossAmount, CURRENCY))}</span>
                        )}
                      </p>
                      {p.notes && <p className="text-xs text-muted-foreground italic">{p.notes}</p>}
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={() => setEditingPayout(p)}
                        aria-label={t.tradingAccounts.payoutEdit}
                        title={t.tradingAccounts.payoutEdit}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeletePayout(p.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <EditPayoutDialog
              payout={editingPayout}
              onClose={() => setEditingPayout(null)}
              onSaved={() => { refetchPayouts(); onRefetch(); }}
            />

            {addingPayout ? (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <PayoutAmountFields values={payoutValues} onChange={setPayoutValues} idPrefix="sheet-payout" />
                <div className="space-y-1">
                  <Label className="text-xs">{t.tradingAccounts.payoutDate}</Label>
                  <Input
                    type="date"
                    value={payoutDate}
                    onChange={(e) => setPayoutDate(e.target.value)}
                    className="h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t.tradingAccounts.payoutNotes}</Label>
                  <Input
                    placeholder={t.tradingAccounts.payoutNotesPlaceholder}
                    value={payoutNotes}
                    onChange={(e) => setPayoutNotes(e.target.value)}
                    className="h-8"
                  />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddPayout} disabled={payoutLoading}>
                    {payoutLoading ? "..." : t.tradingAccounts.save}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAddingPayout(false)}>
                    {t.tradingAccounts.cancel}
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setAddingPayout(true)}>
                <Plus className="h-3.5 w-3.5" />
                {t.tradingAccounts.addPayout}
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Group Section ────────────────────────────────────────────────────────────

function GroupSection({
  group,
  allInactive,
  onEditGroup,
  onDeleteGroup,
  onEditAccount,
  onDeleteAccount,
  onToggleActive,
  onRequestDeactivate,
  onOpenDetail,
  onFundedTransition,
  isPro,
  onConnect,
}: {
  group: EnrichedGroup;
  allInactive?: boolean;
  onEditGroup: (g: EnrichedGroup) => void;
  onDeleteGroup: (g: EnrichedGroup) => void;
  onEditAccount: (a: EnrichedAccount) => void;
  onDeleteAccount: (a: EnrichedAccount) => void;
  onToggleActive: (a: EnrichedAccount) => void;
  onRequestDeactivate: (a: EnrichedAccount) => void;
  onOpenDetail: (a: EnrichedAccount) => void;
  onFundedTransition: (a: EnrichedAccount) => void;
  isPro?: boolean;
  onConnect?: (accountId: string) => void;
}) {
  const t = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(!allInactive);
  const isPositive = group.consolidatedPnl >= 0;

  const effectiveTemplate = group.propFirmTemplate;

  return (
    <div className={`rounded-xl border border-border bg-card/50 overflow-hidden${allInactive ? " opacity-60" : ""}`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-muted-foreground shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {expanded
            ? <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
            : <Folder className="h-4 w-4 text-muted-foreground shrink-0" />}
          <span className="font-semibold text-sm">{group.name}</span>
          {effectiveTemplate && <PropFirmBadge template={effectiveTemplate} size="xs" />}
          <span className="text-xs text-muted-foreground">
            {group.activeCount}/{group.totalCount} {t.tradingAccounts.groupActiveAccounts}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.groupConsolidatedPnl}</p>
            <p className={`text-sm font-semibold font-mono ${isPositive ? "text-pnlPositive" : "text-pnlNegative"}`}>
              {isPositive ? "+" : ""}{group.consolidatedPnl.toFixed(2)}
            </p>
          </div>
          <Button
            variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); navigate(`/account-group/${group.id}`); }}
            title={t.tradingAccounts.groupDetailTitle}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); onEditGroup(group); }}
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); onDeleteGroup(group); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-border/50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {group.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground col-span-full py-2">{t.tradingAccounts.noAccounts}</p>
          ) : (
            group.accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                onEdit={onEditAccount}
                onDelete={onDeleteAccount}
                onToggleActive={onToggleActive}
                onRequestDeactivate={onRequestDeactivate}
                onOpenDetail={onOpenDetail}
                onFundedTransition={onFundedTransition}
                isPro={isPro}
                onConnect={onConnect}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Add Accounts Modal ────────────────────────────────────────────────────────

interface BulkAccountRow {
  // Identité stable d'une ligne, indépendante de sa position.
  //
  // Les lignes étaient identifiées par leur index, à la fois pour la `key` React
  // et pour indexer `customBalances`. Supprimer une ligne décale toutes les
  // suivantes : React réutilisait alors le mauvais sous-arbre et démontait les
  // portails Radix voisins (les Select de palier et de statut) dans un même
  // commit — d'où la modale qui se ferme toute seule en pleine saisie — et le
  // montant personnalisé d'une ligne se rattachait silencieusement à une autre.
  id: string;
  name: string;
  initialBalance: string;
  status: AccountStatus;
  challengeCost: string;
}

let rowIdCounter = 0;
// Compteur monotone plutôt que crypto.randomUUID : disponible partout (vieux
// Safari, WebViews, contextes non sécurisés) et suffisant, l'id ne servant qu'à
// distinguer les lignes d'un même wizard.
const nextRowId = () => `row-${++rowIdCounter}`;

const EMPTY_ROW = (isPropFirm: boolean): BulkAccountRow => ({
  id: nextRowId(),
  name: "",
  initialBalance: "",
  status: isPropFirm ? "challenge" : "live",
  challengeCost: "",
});

// Broker/prop-firm logos are light artwork meant for dark backgrounds, so the
// chip keeps a dark backdrop in both themes to stay visible in light mode.
function LogoWithFallback({ src, fallback }: { src: string; fallback: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex h-12 w-[88px] items-center justify-center rounded-md bg-[#0F1729] px-2">
      {failed ? (
        <span className="text-xs font-semibold text-white">{fallback}</span>
      ) : (
        <img
          src={src}
          alt={fallback}
          className="max-h-8 max-w-full object-contain"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

export function AddAccountsModal({
  open,
  onClose,
  groups,
  templates,
  existingAccounts,
  onSuccess,
  canUseMetaApi,
  onUpgradeRequest,
  tradovateConnections,
  onAddToConnection,
  onConnectNewPropFirm,
}: {
  open: boolean;
  onClose: () => void;
  groups: EnrichedGroup[];
  templates: PropFirmTemplateSnapshot[];
  existingAccounts: { name: string }[];
  onSuccess: () => void;
  canUseMetaApi: boolean;
  onUpgradeRequest: () => void;
  // Tradovate: one connection per prop firm (1 OAuth login = 1 prop firm), N
  // accounts each. The "sync" choice lets the user either add accounts to an
  // existing prop-firm connection (no OAuth) or connect a NEW prop firm (OAuth).
  tradovateConnections: any[];
  onAddToConnection: (connId: string) => void;
  onConnectNewPropFirm: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [accountKind, setAccountKind] = useState<"personal" | "tradovate" | "topstepx" | "metatrader">("personal");
  // Tradovate card sub-mode: manual bulk import (default) vs OAuth auto-sync (Max)
  const [tradovateMode, setTradovateMode] = useState<"manual" | "sync">("manual");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedAccountTypeId, setSelectedAccountTypeId] = useState<string | null>(null);
  const [rows, setRows] = useState<BulkAccountRow[]>([EMPTY_ROW(false)]);
  // Saisie libre du montant, par identifiant de ligne, quand le palier
  // "__custom__" est choisi. Séparé de `rows` pour que la ligne conserve le
  // marqueur "__custom__" (et reste donc invalide) tant que rien n'est saisi.
  //
  // Indexé sur `row.id` et non sur la position : supprimer une ligne décalait
  // les suivantes et le montant saisi se retrouvait appliqué à un autre compte.
  const [customBalances, setCustomBalances] = useState<Record<string, string>>({});
  const [groupOption, setGroupOption] = useState<"none" | "existing" | "new">("none");
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupTemplateId, setNewGroupTemplateId] = useState<string | null>(null);
  const [newGroupStrategyId, setNewGroupStrategyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [bulkNameError, setBulkNameError] = useState("");

  // Tradovate challenge price: shared across all accounts in the batch
  const [challengePriceMode, setChallengePriceMode] = useState<"total" | "unit">("unit");
  const [challengePriceValue, setChallengePriceValue] = useState("");
  // Dates communes du lot pour la saisie de comptes historiques (repliées par
  // défaut : le cas courant reste la création d'un compte du jour).
  const [showHistoricalFields, setShowHistoricalFields] = useState(false);
  const [bulkPurchasedAt, setBulkPurchasedAt] = useState("");
  const [bulkClosedAt, setBulkClosedAt] = useState("");

  // MetaTrader-specific state
  const [mtLogin, setMtLogin] = useState("");
  const [mtPassword, setMtPassword] = useState("");
  const [mtServer, setMtServer] = useState("");
  const [mtPlatform, setMtPlatform] = useState<"mt4" | "mt5">("mt5");
  const [mtAccountName, setMtAccountName] = useState("");
  const [mtAccountStatus, setMtAccountStatus] = useState<"demo" | "live" | "challenge">("live");
  // Tracks the account created in step 3 so a connection retry doesn't re-create it
  const [mtCreatedAccountId, setMtCreatedAccountId] = useState<string | null>(null);
  const [mtConnectionError, setMtConnectionError] = useState<string | null>(null);

  const { data: strategiesData, refetch: refetchWizardStrategies } = useQuery(getTradingStrategiesByUser);
  const allStrategies = (strategiesData as any[]) ?? [];
  const [newGroupStrategyName, setNewGroupStrategyName] = useState("");
  const [newGroupStrategyLoading, setNewGroupStrategyLoading] = useState(false);

  const handleAddGroupStrategy = async () => {
    const trimmed = newGroupStrategyName.trim();
    if (!trimmed) return;
    setNewGroupStrategyLoading(true);
    try {
      const created = await createTradingStrategy({ name: trimmed, color: "#71717a" }) as any;
      await refetchWizardStrategies();
      setNewGroupStrategyId(created.id);
      setNewGroupStrategyName("");
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("existe déjà") || msg.includes("already exists")) {
        toast({ title: t.tradingAccounts.strategyExists, variant: "destructive" });
      } else {
        toast({ title: t.tradingAccounts.strategyCreateError, description: msg, variant: "destructive" });
      }
    } finally {
      setNewGroupStrategyLoading(false);
    }
  };

  const isTradovate = accountKind === "tradovate";
  const isTopstepX = accountKind === "topstepx";
  const isMetaTrader = accountKind === "metatrader";
  // Prop-firm accounts (Tradovate + Topstep X) share the same wizard flow:
  // prop firm template, account types, tiers, challenge price.
  const isPropFirm = isTradovate || isTopstepX;

  // Topstep X always uses the Topstep prop firm template, locked (not user-selectable).
  // We accept either the system template (slug "topstep") or a custom template named
  // "Topstep" (some users created one manually before the system seed existed).
  const topstepTemplate = useMemo(
    () =>
      templates.find((tpl) => tpl.slug === "topstep") ??
      templates.find(
        (tpl) => typeof tpl.name === "string" && tpl.name.trim().toLowerCase() === "topstep",
      ) ??
      null,
    [templates],
  );
  useEffect(() => {
    if (isTopstepX && topstepTemplate && selectedTemplateId !== topstepTemplate.id) {
      setSelectedTemplateId(topstepTemplate.id);
    }
  }, [isTopstepX, topstepTemplate, selectedTemplateId]);

  // For a prop firm template that has multiple account types, we insert an extra step
  const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId) ?? null;
  const templateAccountTypes = (selectedTemplate?.accountTypes ?? []).filter((at) => at.isActive);
  const hasAccountTypeStep = isPropFirm && templateAccountTypes.length > 1;

  // Tiers available for the selected account type (or all tiers if no account type step)
  const availableTiers = useMemo(() => {
    if (!isPropFirm || !selectedTemplate) return [];
    if (selectedAccountTypeId) {
      const at = templateAccountTypes.find((a) => a.id === selectedAccountTypeId);
      return at?.tiers ?? [];
    }
    return templateAccountTypes.flatMap((at) => at.tiers);
  }, [isPropFirm, selectedTemplate, selectedAccountTypeId, templateAccountTypes]);

  // Modèle du type choisi. Sans étape de choix de type (un seul type actif),
  // c'est celui du type unique ; sinon celui sélectionné.
  const selectedPricingModel = normalizePricingModel(
    (selectedAccountTypeId
      ? templateAccountTypes.find((a) => a.id === selectedAccountTypeId)
      : templateAccountTypes.length === 1
        ? templateAccountTypes[0]
        : null
    )?.pricingModel,
  );
  const directFunded = isPropFirm && isDirectFunded(selectedPricingModel);
  const subscription = isPropFirm && isSubscription(selectedPricingModel);

  // Ids des lignes basculées AUTOMATIQUEMENT "challenge" -> "pa" par l'effet
  // ci-dessous, pour pouvoir les réhabiliter si l'utilisateur revient à un
  // type qui autorise le challenge (ex : il change d'avis sur le type de
  // compte après coup). Ne contient jamais une ligne que l'utilisateur a
  // choisie explicitement.
  const directFundedAutoSwitched = useRef<Set<string>>(new Set());

  // Un direct funded ne peut pas être créé en challenge (refusé par le serveur) :
  // on bascule les lignes plutôt que de laisser l'utilisateur buter sur
  // l'erreur. Réversible : si l'utilisateur revient sur un type one_time /
  // subscription, les lignes que CETTE bascule avait elle-même posées à "pa"
  // reviennent à "challenge" (voir wizardRowStatus.ts). Un dernier filet de
  // normalisation à la soumission couvre les lignes qui n'ont jamais transité
  // par cet effet (reset de kind, duplication...).
  useEffect(() => {
    // Calculé HORS du updater `setRows` : muter `directFundedAutoSwitched.current`
    // à l'intérieur d'un updater fonctionnel n'est pas idempotent sous le
    // double-appel de React StrictMode (l'updater peut être invoqué deux fois
    // pour le même rendu, ce qui basculerait la ref deux fois de suite).
    const result = toggleDirectFundedRows(rows, directFunded, directFundedAutoSwitched.current);
    directFundedAutoSwitched.current = result.autoSwitchedIds;
    setRows(result.rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directFunded]);

  // Logical step mapping: visual steps are consecutive, but we insert the account-type step only when needed
  // steps: 1=kind/propfirm, [2=accountType if hasAccountTypeStep], N-1=rows, N=group
  const totalSteps = isMetaTrader ? 3 : hasAccountTypeStep ? 4 : 3;

  const resetAndClose = () => {
    setStep(1);
    setAccountKind("personal");
    setTradovateMode("manual");
    setSelectedTemplateId(null);
    setSelectedAccountTypeId(null);
    setRows([EMPTY_ROW(false)]);
    setCustomBalances({});
    setGroupOption("none");
    setSelectedGroupId("");
    setNewGroupName("");
    setNewGroupTemplateId(null);
    setNewGroupStrategyId(null);
    setNewGroupStrategyName("");
    setChallengePriceMode("unit");
    setChallengePriceValue("");
    setMtLogin("");
    setMtPassword("");
    setMtServer("");
    setMtPlatform("mt5");
    setMtAccountName("");
    setMtAccountStatus("live");
    setMtCreatedAccountId(null);
    setMtConnectionError(null);
    setBulkNameError("");
    onClose();
  };

  const updateRow = (index: number, field: keyof BulkAccountRow, value: string) => {
    if (field === "name") setBulkNameError("");
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleKindChange = (kind: "personal" | "tradovate" | "topstepx" | "metatrader") => {
    const isPropFirmKind = kind === "tradovate" || kind === "topstepx";
    setAccountKind(kind);
    setTradovateMode("manual");
    // Topstep X locks the template to "topstep" (set by effect); other kinds reset it.
    setSelectedTemplateId(kind === "topstepx" ? (topstepTemplate?.id ?? null) : null);
    setSelectedAccountTypeId(null);
    // NE PAS pré-basculer la ligne ici (régression revue Task 14, e04b62d8) :
    // `directFunded` lu à cet endroit est encore la valeur de l'AVANT-reset
    // (React n'a pas encore recalculé le rendu avec le nouveau kind/template),
    // donc pré-switcher sur cette valeur pouvait poser "pa" pour le mauvais
    // type — et l'id de cette ligne neuve n'était jamais suivi dans
    // `directFundedAutoSwitched`, donc jamais réhabilité si `directFunded`
    // finissait par redevenir false. Résultat : un compte "challenge" voulu se
    // retrouvait créé FUNDED (fundedAt = maintenant), en silence.
    //
    // La ligne neuve part donc TOUJOURS sur le statut par défaut de EMPTY_ROW.
    // Deux filets la corrigent si besoin, tous deux lus sur `directFunded` déjà
    // RECALCULÉ pour le nouveau kind :
    //  - l'effet ci-dessus, si `directFunded` change de valeur au rendu suivant ;
    //  - `normalizeStatusForSubmit` à la soumission, qui couvre aussi le cas où
    //    `directFunded` était DÉJÀ vrai avant ce reset et le reste (l'effet ne
    //    se redéclenche pas faute de changement de valeur, mais aucun compte
    //    "challenge" ne peut alors atteindre le serveur).
    setRows([EMPTY_ROW(isPropFirmKind)]);
  };

  // customBalances est indexé sur `row.id`, donc filtrer ne fausse plus rien.
  const validRows = rows.filter((row) => row.name.trim());

  // Une taille de compte manquante partait silencieusement à 0 : le palier prop
  // firm ne pouvait plus être résolu et toutes les cards du compte restaient
  // vides. "__custom__" est le marqueur du choix "Montant personnalisé" tant que
  // l'utilisateur n'a rien saisi — il ne doit jamais atteindre la soumission.
  //
  // Valeur effective d'une ligne : le montant libre quand "__custom__" est
  // choisi, sinon le palier sélectionné. Source unique pour la validation ET la
  // soumission, afin qu'elles ne puissent pas diverger.
  const resolveBalance = (r: BulkAccountRow) =>
    (r.initialBalance === "__custom__" ? (customBalances[r.id] ?? "") : r.initialBalance).trim();

  const hasInvalidBalance = (r: BulkAccountRow) => {
    const parsed = parseFloat(resolveBalance(r));
    return !Number.isFinite(parsed) || parsed <= 0;
  };
  const hasMissingBalance = rows.some(hasInvalidBalance);

  const handleSubmit = async () => {
    if (isMetaTrader) {
      if (!mtAccountName.trim() || !mtLogin.trim() || !mtPassword.trim() || !mtServer.trim()) return;
      setLoading(true);
      setMtConnectionError(null);
      try {
        // If the account was already created in a previous failed attempt, reuse it
        let accountId = mtCreatedAccountId;
        if (!accountId) {
          const result = await createAccounts({
            accounts: [{ name: mtAccountName.trim(), initialBalance: 0, status: mtAccountStatus, propFirmTemplateId: null, challengeCost: null }],
            groupId: null,
            newGroupName: null,
            newGroupPropFirmTemplateId: null,
          });
          accountId = result.ids?.[0] ?? null;
          if (accountId) setMtCreatedAccountId(accountId);
        }
        if (accountId) {
          await connectMetaApiAccount({ accountId, login: mtLogin.trim(), password: mtPassword, server: mtServer.trim(), platform: mtPlatform });
          trackMetaApiConnected();
        }
        trackAccountCreated(mtAccountStatus);
        toast({ title: t.tradingAccounts.mtAccountCreated });
        // Close before refetching — see the removeChild note in the bulk-create
        // handler below: same-tick unmount + re-render races the Select portals.
        resetAndClose();
        setTimeout(() => onSuccess(), 0);
      } catch (err: any) {
        // Show inline error so the user can fix credentials without losing them
        const rawMsg: string = err?.message ?? "";
        const cleanMsg = rawMsg.replace(/^Connection failed:\s*/i, "").trim();
        setMtConnectionError(cleanMsg || t.tradingAccounts.mtAccountError);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (validRows.length === 0) return;
    // Filet de sécurité : le bouton est déjà désactivé dans ce cas, mais on ne
    // veut sous aucun chemin envoyer un compte à 0 au serveur.
    if (hasMissingBalance) {
      setBulkNameError(t.tradingAccounts.accountSizeRequired);
      return;
    }

    // Check for duplicate names within the wizard rows
    const trimmedNames = validRows.map((r) => r.name.trim().toLowerCase());
    const hasDuplicateWithinRows = trimmedNames.length !== new Set(trimmedNames).size;
    if (hasDuplicateWithinRows) {
      setBulkNameError(t.tradingAccounts.duplicateAccountName);
      return;
    }
    setBulkNameError("");
    setLoading(true);
    // Compute per-account challenge cost from the shared price field
    const sharedPriceRaw = challengePriceValue ? parseFloat(challengePriceValue) : null;
    const perAccountChallengeCost =
      isPropFirm && sharedPriceRaw != null && !isNaN(sharedPriceRaw)
        ? challengePriceMode === "total"
          ? sharedPriceRaw / validRows.length
          : sharedPriceRaw
        : null;

    try {
      const result = await createAccounts({
        accounts: validRows.map((r) => ({
          name: r.name.trim(),
          initialBalance: parseFloat(resolveBalance(r)),
          // Filet de sécurité : une ligne "challenge" ne doit JAMAIS partir
          // vers un type direct funded, quel que soit le chemin qui l'a
          // produite (reset de kind, duplication de ligne...). L'effet
          // ci-dessus couvre le cas courant, mais pas les lignes recréées
          // après coup pendant que `directFunded` est déjà vrai.
          status: normalizeStatusForSubmit(r.status, directFunded),
          propFirmTemplateId: isPropFirm ? selectedTemplateId : null,
          propFirmAccountTypeId: isPropFirm ? selectedAccountTypeId : null,
          propFirm: isTradovate ? "tradovate" : isTopstepX ? "topstep" : null,
          challengeCost: isPropFirm ? perAccountChallengeCost : (r.challengeCost ? parseFloat(r.challengeCost) : null),
          purchasedAt: bulkPurchasedAt ? new Date(bulkPurchasedAt).toISOString() : null,
          closedAt: bulkClosedAt ? new Date(bulkClosedAt).toISOString() : null,
        })),
        groupId: groupOption === "existing" ? selectedGroupId || null : null,
        newGroupName: groupOption === "new" && newGroupName.trim() ? newGroupName.trim() : null,
        newGroupPropFirmTemplateId: groupOption === "new" ? (isPropFirm ? selectedTemplateId : newGroupTemplateId) : null,
        newGroupStrategyId: newGroupStrategyId,
      });

      if (result.errors && result.errors.length > 0) {
        setBulkNameError(t.tradingAccounts.duplicateAccountName);
        return;
      }

      trackAccountCreated(validRows[0]?.status ?? 'live');
      if (groupOption === 'new' && newGroupName.trim()) {
        trackAccountGroupCreated();
      }
      toast({
        title: t.tradingAccounts.accountsCreated,
        description: t.tradingAccounts.accountsCreatedDescription.replace("{count}", String(validRows.length)),
      });
      // Order matters. Closing the dialog unmounts its Radix SelectPortal
      // subtree; onSuccess() refetches and re-renders the account list. Run in
      // the same tick, React commits both deletions in one pass and the two
      // portals race to detach the same nodes — DOMException removeChild, which
      // the page-level boundary turns into the "Something went wrong" screen.
      // Hit hardest on the FIRST account, where onSuccess() also swaps the
      // empty state for the populated list (a much bigger commit).
      // Close first, refetch on the next tick: two separate commits, no race.
      resetAndClose();
      setTimeout(() => onSuccess(), 0);
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("existe déjà") || msg.includes("already exists")) {
        setBulkNameError(t.tradingAccounts.duplicateAccountName);
      } else {
        toast({ title: t.tradingAccounts.error, description: msg || t.tradingAccounts.bulkCreateError, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  const isAccountNamesStep = !isMetaTrader && ((step === 2 && !hasAccountTypeStep) || (step === 3 && hasAccountTypeStep));

  const handleNext = () => {
    if (isAccountNamesStep) {
      const hasEmptyName = rows.some((r) => !r.name.trim());
      if (hasEmptyName) {
        setBulkNameError(t.tradingAccounts.accountNameRequired);
        return;
      }
      const trimmedNames = rows.map((r) => r.name.trim().toLowerCase());
      const hasDuplicateWithinRows = trimmedNames.length !== new Set(trimmedNames).size;
      const existingNames = new Set(existingAccounts.map((a) => a.name.toLowerCase()));
      const hasConflictWithExisting = trimmedNames.some((n) => existingNames.has(n));
      if (hasDuplicateWithinRows || hasConflictWithExisting) {
        setBulkNameError(t.tradingAccounts.duplicateAccountName);
        return;
      }
      if (hasMissingBalance) {
        setBulkNameError(t.tradingAccounts.accountSizeRequired);
        return;
      }
      setBulkNameError("");
    }
    setStep((s) => s + 1);
  };

  // Step labels differ by kind
  const stepLabels = isMetaTrader
    ? [t.tradingAccounts.accountType, t.tradingAccounts.mtCredentialsStep, t.tradingAccounts.mtAccountDetailsStep]
    : hasAccountTypeStep
      ? [t.tradingAccounts.selectPropFirmStep, t.tradingAccounts.selectAccountTypeStep, t.tradingAccounts.enterAccountsStep, t.tradingAccounts.assignGroupStep]
      : [t.tradingAccounts.selectPropFirmStep, t.tradingAccounts.enterAccountsStep, t.tradingAccounts.assignGroupStep];

  // Next button disabled state
  const nextDisabled =
    (step === 1 && isTopstepX && !selectedTemplateId) || // Topstep template must resolve
    (step === 2 && hasAccountTypeStep && !selectedAccountTypeId) ||
    (step === 2 && isMetaTrader && (!mtLogin.trim() || !mtPassword.trim() || !mtServer.trim())) ||
    (isAccountNamesStep && (validRows.length === 0 || hasMissingBalance));

  const submitDisabled = loading ||
    (isMetaTrader ? (!mtAccountName.trim()) : (validRows.length === 0 || hasMissingBalance));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && resetAndClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.addAccounts}</DialogTitle>
          <DialogDescription>{t.tradingAccounts.addAccountsDescription}</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-4">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div key={s} className="flex items-center gap-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                s === step ? "bg-primary text-primary-foreground" : s < step ? "bg-primary/30 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                {s}
              </div>
              <span className="text-xs text-muted-foreground hidden sm:inline">{stepLabels[s - 1]}</span>
              {s < totalSteps && <div className="w-8 h-px bg-border mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 1: Account kind */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t.tradingAccounts.accountType}</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(["personal", "tradovate", "topstepx", "metatrader"] as const).map((kind) => {
                  // MetaTrader sync is temporarily unavailable: the tile is fully
                  // disabled (greyed out, non-clickable) and flagged "Soon" until
                  // the feature ships. No upgrade path — it's not a plan gate.
                  const isMetaTraderDisabled = kind === "metatrader";
                  return (
                    <button
                      key={kind}
                      type="button"
                      disabled={isMetaTraderDisabled}
                      aria-disabled={isMetaTraderDisabled}
                      onClick={() => {
                        if (isMetaTraderDisabled) return;
                        handleKindChange(kind);
                      }}
                      className={`relative flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-sm font-medium transition-colors ${
                        isMetaTraderDisabled
                          ? "border-border text-muted-foreground/50 opacity-60 cursor-not-allowed"
                          : accountKind === kind
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-foreground/30"
                      }`}
                    >
                      {isMetaTraderDisabled && (
                        <span className="absolute -top-2 -right-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground leading-tight">
                          {t.common.soon}
                        </span>
                      )}
                      {kind === "personal" ? (
                        <span className="text-3xl">🏦</span>
                      ) : kind === "tradovate" ? (
                        <LogoWithFallback src="/logos/tradovate.png" fallback={t.tradingAccounts.tradovateAccount} />
                      ) : kind === "topstepx" ? (
                        <LogoWithFallback src="/logos/topstepx.png" fallback={t.tradingAccounts.topstepxAccount} />
                      ) : (
                        <LogoWithFallback src="/logos/metatrader.png" fallback={t.tradingAccounts.metatraderAccount} />
                      )}
                      {kind === "personal" && (
                        <span className="text-center leading-tight">{t.tradingAccounts.personalAccount}</span>
                      )}
                      {kind !== "personal" && (
                        <span className="text-xs font-normal opacity-70 text-center">
                          {kind === "tradovate"
                            ? t.tradingAccounts.tradovateAccountDescription
                            : kind === "topstepx"
                            ? t.tradingAccounts.topstepxAccountDescription
                            : t.tradingAccounts.metatraderAccountDescription}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {isTradovate && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.tradingAccounts.accountType}</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setTradovateMode("manual")}
                      className={`relative flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left text-sm font-medium transition-colors ${
                        tradovateMode === "manual"
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-foreground/30"
                      }`}
                    >
                      <span>{t.tradingAccounts.tradovateModeManual}</span>
                      <span className="text-xs font-normal opacity-70">
                        {t.tradingAccounts.tradovateAccountDescription}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!canUseMetaApi) { onUpgradeRequest(); return; }
                        // One connection per prop firm. If none exist yet, go
                        // straight to a new-prop-firm OAuth; otherwise reveal
                        // the target-choice panel below.
                        if (tradovateConnections.length === 0) { onConnectNewPropFirm(); return; }
                        setTradovateMode("sync");
                      }}
                      className={`relative flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left text-sm font-medium transition-colors ${
                        tradovateMode === "sync"
                          ? "border-primary bg-primary/10 text-primary"
                          : canUseMetaApi
                          ? "border-border text-muted-foreground hover:border-foreground/30"
                          : "border-border text-muted-foreground/50 opacity-60 cursor-pointer"
                      }`}
                    >
                      <span className="absolute -top-2 -right-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground leading-tight">
                        Max
                      </span>
                      <span>{t.tradingAccounts.tradovateModeSync}</span>
                      <span className="text-xs font-normal opacity-70">
                        {canUseMetaApi ? t.tradovate.syncChoiceDescription : t.tradovate.maxPlanRequired}
                      </span>
                    </button>
                  </div>
                </div>

                {tradovateMode === "sync" && tradovateConnections.length > 0 && (
                  <div className="space-y-2">
                    <Label>{t.tradovate.chooseTradovateTarget}</Label>
                    <div className="space-y-2">
                      {tradovateConnections.map((conn: any) => (
                        <button
                          key={conn.id}
                          type="button"
                          onClick={() => onAddToConnection(conn.id)}
                          className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/60 hover:bg-primary/5"
                        >
                          <span className="flex items-center gap-2 truncate">
                            {conn.propFirmTemplate ? (
                              <span
                                className="inline-block h-3 w-3 shrink-0 rounded-full"
                                style={{ backgroundColor: conn.propFirmTemplate.color }}
                              />
                            ) : (
                              <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            )}
                            <span className="truncate">
                              {conn.propFirmTemplate?.name ?? t.tradovate.connectionBannerTitle}
                            </span>
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">{t.tradovate.addAccountHere}</span>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={onConnectNewPropFirm}
                        className="flex w-full items-center gap-2 rounded-md border border-dashed border-border bg-transparent px-3 py-2.5 text-left text-sm text-primary transition-colors hover:border-primary/60 hover:bg-primary/5"
                      >
                        <Plus className="h-3.5 w-3.5 shrink-0" />
                        <span>{t.tradovate.connectNewPropFirm}</span>
                      </button>
                    </div>
                  </div>
                )}

                {tradovateMode === "manual" && (
                  <div className="space-y-2">
                    <Label>{t.tradingAccounts.selectPropFirm}</Label>
                    <PropFirmSelector
                      value={selectedTemplateId}
                      onChange={setSelectedTemplateId}
                      templates={templates}
                      allowNone={false}
                    />
                    {selectedTemplate && (
                      <p className="text-xs text-muted-foreground">
                        {t.tradingAccounts.propFirmRulesApplied.split("{name}")[0]}<span className="font-medium">{selectedTemplate.name}</span>{t.tradingAccounts.propFirmRulesApplied.split("{name}")[1]}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Topstep X: prop firm is locked to Topstep, shown read-only */}
            {isTopstepX && (
              <div className="space-y-2">
                <Label>{t.tradingAccounts.selectPropFirm}</Label>
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                  <span className="font-medium text-foreground">
                    {topstepTemplate?.name ?? "Topstep"}
                  </span>
                </div>
                {topstepTemplate && (
                  <p className="text-xs text-muted-foreground">
                    {t.tradingAccounts.propFirmRulesApplied.split("{name}")[0]}<span className="font-medium">{topstepTemplate.name}</span>{t.tradingAccounts.propFirmRulesApplied.split("{name}")[1]}
                  </p>
                )}
              </div>
            )}

          </div>
        )}

        {/* Step 2 MetaTrader: MT credentials */}
        {step === 2 && isMetaTrader && (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>{t.tradingAccounts.mtLogin}</Label>
              <Input
                placeholder={t.tradingAccounts.mtLoginPlaceholder}
                value={mtLogin}
                onChange={(e) => setMtLogin(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <Label>{t.tradingAccounts.mtPassword}</Label>
              <Input
                type="password"
                value={mtPassword}
                onChange={(e) => setMtPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1">
              <Label>{t.tradingAccounts.mtServer}</Label>
              <Input
                placeholder={t.tradingAccounts.mtServerPlaceholder}
                value={mtServer}
                onChange={(e) => setMtServer(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>{t.tradingAccounts.mtPlatform}</Label>
              <Select value={mtPlatform} onValueChange={(v) => setMtPlatform(v as "mt4" | "mt5")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mt5">MetaTrader 5</SelectItem>
                  <SelectItem value="mt4">MetaTrader 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              {t.metaApi.connectModal.description}
            </div>
          </div>
        )}

        {/* Step 3 MetaTrader: Account details */}
        {step === 3 && isMetaTrader && (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>{t.tradingAccounts.accountName}</Label>
              <Input
                placeholder="Mon compte MT5"
                value={mtAccountName}
                onChange={(e) => { setMtAccountName(e.target.value); setMtConnectionError(null); }}
                disabled={!!mtCreatedAccountId}
              />
            </div>
            <div className="space-y-1">
              <Label>{t.tradingAccounts.accountStatus}</Label>
              <Select value={mtAccountStatus} onValueChange={(v) => setMtAccountStatus(v as "demo" | "live" | "challenge")} disabled={!!mtCreatedAccountId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">{t.tradingAccounts.statusLive}</SelectItem>
                  <SelectItem value="demo">{t.tradingAccounts.statusDemo}</SelectItem>
                  <SelectItem value="challenge">{t.tradingAccounts.statusChallenge}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mtConnectionError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 space-y-1">
                <p className="text-sm text-destructive font-medium">{t.tradingAccounts.mtAccountError}</p>
                <p className="text-xs text-destructive/80">{t.tradingAccounts.mtConnectionRetryHint}</p>
              </div>
            )}
            {mtCreatedAccountId && !mtConnectionError && (
              <p className="text-xs text-muted-foreground">{t.metaApi.connectModal.description}</p>
            )}
          </div>
        )}

        {/* Step 2 Tradovate with multiple account types: pick account type */}
        {step === 2 && hasAccountTypeStep && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {t.tradingAccounts.selectAccountTypeDescription.replace("{name}", selectedTemplate?.name ?? "")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {templateAccountTypes.map((at) => (
                <button
                  key={at.id}
                  type="button"
                  onClick={() => setSelectedAccountTypeId(at.id)}
                  className={`flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-sm font-medium transition-colors text-left ${
                    selectedAccountTypeId === at.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-foreground hover:border-foreground/30"
                  }`}
                >
                  <span className="font-semibold">{at.name}</span>
                  {at.tiers.length > 0 && (
                    <span className="text-xs opacity-70">{at.tiers.map((tier) => tier.label).join(" · ")}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2 standard (no account type step) or step 3 (with account type step): Account rows */}
        {((step === 2 && !isMetaTrader && !hasAccountTypeStep) || (step === 3 && hasAccountTypeStep && !isMetaTrader)) && (
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium px-1">
              <span className={`col-span-5 ${validRows.length === 0 ? "text-destructive" : "text-muted-foreground"}`}>
                {t.tradingAccounts.accountName}
              </span>
              <span className={`col-span-4 ${hasMissingBalance ? "text-destructive" : "text-muted-foreground"}`}>{isPropFirm && availableTiers.length > 0 ? t.tradingAccounts.selectTierBalance : t.tradingAccounts.initialBalance}</span>
              <span className="col-span-2 text-muted-foreground">{t.tradingAccounts.accountStatus}</span>
              <span className="col-span-1" />
            </div>
            {bulkNameError && bulkNameError !== t.tradingAccounts.accountNameRequired && (
              <p className="text-xs text-destructive px-1">{bulkNameError}</p>
            )}
            {rows.map((row, i) => (
              // key = identité stable de la ligne. Avec l'index, supprimer une
              // ligne réassignait les clés et React réutilisait le mauvais
              // sous-arbre, démontant des portails Radix voisins dans le même
              // commit (modale qui se ferme en pleine saisie).
              <div key={row.id} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-5">
                  <Input
                    placeholder={`Compte ${i + 1}`}
                    value={row.name}
                    onChange={(e) => { updateRow(i, "name", e.target.value); if (rows.every((r, j) => j === i ? e.target.value.trim() : r.name.trim())) setBulkNameError(""); }}
                    className={!row.name.trim() && bulkNameError === t.tradingAccounts.accountNameRequired ? "border-destructive focus-visible:ring-destructive" : ""}
                  />
                </div>
                <div className="col-span-4">
                  {isPropFirm && availableTiers.length > 0 ? (
                    <Select
                      value={row.initialBalance}
                      onValueChange={(v) => updateRow(i, "initialBalance", v)}
                    >
                      <SelectTrigger className="text-xs h-9"><SelectValue placeholder={t.tradingAccounts.selectTierBalance} /></SelectTrigger>
                      <SelectContent>
                        {availableTiers.map((tier) => (
                            // Use the tier's nominal account size as the starting balance.
                            // minBalance is only a matching bound (e.g. 295000 for the $300k tier),
                            // so it would create the account with the wrong balance.
                            <SelectItem key={tier.id} value={String(tier.accountSize ?? tier.minBalance)}>
                              {tier.label}
                            </SelectItem>
                          ))}
                        <SelectItem value="__custom__">{t.tradingAccounts.customBalance}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type="number"
                      placeholder="50000"
                      value={row.initialBalance}
                      onChange={(e) => updateRow(i, "initialBalance", e.target.value)}
                    />
                  )}
                </div>
                <div className="col-span-2">
                  <Select value={row.status} onValueChange={(v) => updateRow(i, "status", v)}>
                    <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {!directFunded && <SelectItem value="challenge">{t.tradingAccounts.statusChallenge}</SelectItem>}
                      <SelectItem value="pa">{t.tradingAccounts.statusPa}</SelectItem>
                      <SelectItem value="live">{t.tradingAccounts.statusLive}</SelectItem>
                      <SelectItem value="demo">{t.tradingAccounts.statusDemo}</SelectItem>
                      <SelectItem value="inactive">{t.tradingAccounts.statusInactive}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 flex justify-end">
                  {rows.length > 1 && (
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setRows((prev) => prev.filter((r) => r.id !== row.id));
                        // Libère le montant libre de la ligne supprimée pour
                        // qu'il ne soit pas réutilisé si un id était recyclé.
                        setCustomBalances((prev) => {
                          if (!(row.id in prev)) return prev;
                          const { [row.id]: _removed, ...rest } = prev;
                          return rest;
                        });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {/* Custom balance input when "__custom__" selected.
                    Contrôlé : la ligne garde "__custom__" tant que le champ est
                    vide, ce qui laisse hasMissingBalance bloquer l'étape au lieu
                    de créer un compte à 0. */}
                {isPropFirm && row.initialBalance === "__custom__" && (
                  <div className="col-span-12 -mt-1">
                    <Input
                      type="number"
                      placeholder="50000"
                      autoFocus
                      className="h-9"
                      value={customBalances[row.id] ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomBalances((prev) => ({ ...prev, [row.id]: val }));
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
            {rows.length < 20 && (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5"
                onClick={() => {
                  const last = rows[rows.length - 1];
                  const newId = nextRowId();
                  // Si la ligne dupliquée était auto-basculée "pa" par l'effet
                  // ci-dessus, la copie l'est tout autant : sans ça, un retour à
                  // un type qui autorise le challenge ne la réhabiliterait
                  // jamais (son id n'existerait pas dans `autoSwitchedIds`), et
                  // elle resterait "pa" en silence. Calculé hors du updater
                  // `setRows` : muter la ref à l'intérieur ne serait pas
                  // idempotent sous le double-appel de React StrictMode.
                  directFundedAutoSwitched.current = propagateAutoSwitched(
                    directFundedAutoSwitched.current,
                    last.id,
                    newId,
                  );
                  // id neuf : copier `last` tel quel dupliquerait sa clé React.
                  setRows([...rows, { ...last, id: newId, name: "" }]);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t.tradingAccounts.addAnotherAccount}
              </Button>
            )}

            {/* Shared challenge price for prop firm accounts (Tradovate / Topstep X) */}
            {isPropFirm && (
              <div className="border-t pt-3 mt-1 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">
                    {/* Le prix saisi ici devient un ABONNEMENT récurrent
                        uniquement pour une ligne en "challenge" sur un type
                        subscription (voir propFirmSubscription.ts : `subscribes`
                        exige `status === "challenge"`). Une ligne déjà "pa"
                        stocke ce même prix comme un coût ponctuel
                        (`challengeCost`) : lui montrer le libellé "abonnement"
                        mentirait sur ce qui sera réellement créé. */}
                    {subscription && rows.some((r) => r.status === "challenge")
                      ? t.tradingAccounts.subscriptionPriceLabel
                      : t.tradingAccounts.challengeCostLabel}
                  </Label>
                  {rows.length > 1 && (
                    <div className="flex gap-1">
                      {(["unit", "total"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setChallengePriceMode(mode)}
                          className={`px-2.5 py-1 rounded text-xs border transition-colors ${
                            challengePriceMode === mode
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-foreground/30"
                          }`}
                        >
                          {mode === "unit" ? t.tradingAccounts.challengePriceModeUnit : t.tradingAccounts.challengePriceModeTotal}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground pointer-events-none select-none">USD</span>
                    <Input
                      type="number"
                      placeholder={t.tradingAccounts.challengeCostPlaceholder}
                      value={challengePriceValue}
                      onChange={(e) => setChallengePriceValue(e.target.value)}
                      className="h-9 pl-12"
                    />
                  </div>
                {challengePriceMode === "total" && rows.length > 1 && challengePriceValue && (
                  <p className="text-xs text-muted-foreground">
                    ≈ {(parseFloat(challengePriceValue) / rows.length).toFixed(2)} {CURRENCY} / {t.tradingAccounts.challengePriceModeUnit.toLowerCase()}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {subscription && rows.some((r) => r.status === "challenge")
                    ? t.tradingAccounts.subscriptionPriceHint
                    : t.tradingAccounts.challengeCostHint}
                </p>
                {directFunded && (
                  <p className="text-xs text-muted-foreground">{t.tradingAccounts.directFundedHint}</p>
                )}
              </div>
            )}

            {/* Comptes historiques : dates communes à toutes les lignes du lot,
                comme le prix du challenge juste au-dessus. */}
            <div className="border-t pt-3 mt-1 space-y-2">
              <button
                type="button"
                onClick={() => setShowHistoricalFields((v) => !v)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${showHistoricalFields ? "rotate-180" : ""}`}
                />
                {t.tradingAccounts.historicalAccountToggle}
              </button>

              {showHistoricalFields && (
                <div className="space-y-3 pt-1">
                  <p className="text-xs text-muted-foreground">
                    {t.tradingAccounts.historicalAccountHint}
                  </p>
                  <div className="grid grid-cols-1 xsm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm">{t.tradingAccounts.purchasedAtLabel}</Label>
                      <Input
                        type="date"
                        className="h-9"
                        max={toDateInputValue(new Date())}
                        value={bulkPurchasedAt}
                        onChange={(e) => setBulkPurchasedAt(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm">{t.tradingAccounts.closedAtLabel}</Label>
                      <Input
                        type="date"
                        className="h-9"
                        min={bulkPurchasedAt || undefined}
                        max={toDateInputValue(new Date())}
                        value={bulkClosedAt}
                        onChange={(e) => setBulkClosedAt(e.target.value)}
                      />
                    </div>
                  </div>
                  {bulkClosedAt && (
                    <p className="text-xs text-muted-foreground">
                      {t.tradingAccounts.closedAtNotice}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last standard step: Group assignment */}
        {step === totalSteps && !isMetaTrader && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t.tradingAccounts.assignToGroup}</Label>
              <div className="flex gap-2 flex-wrap">
                {(["none", "existing", "new"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                      groupOption === opt
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:border-foreground/40"
                    }`}
                    onClick={() => setGroupOption(opt)}
                  >
                    {opt === "none" ? t.tradingAccounts.noGroupOption : opt === "existing" ? t.tradingAccounts.existingGroup : t.tradingAccounts.createNewGroup}
                  </button>
                ))}
              </div>
            </div>

            {groupOption === "existing" && (
              <div className="space-y-2">
                <Label>{t.tradingAccounts.existingGroup}</Label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger><SelectValue placeholder={t.tradingAccounts.selectGroupPlaceholder} /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {groupOption === "new" && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{t.tradingAccounts.groupName}</Label>
                  <Input
                    placeholder={t.tradingAccounts.groupNamePlaceholder}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                  />
                </div>
                {!isPropFirm && (
                  <div className="space-y-2">
                    <Label>{t.tradingAccounts.groupPropFirm}</Label>
                    <PropFirmSelector
                      value={newGroupTemplateId}
                      onChange={setNewGroupTemplateId}
                      templates={templates}
                    />
                    <p className="text-xs text-muted-foreground">
                      Si définie, les comptes du groupe sans prop firm individuelle hériteront de ces règles.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>{groupOption === "new" ? t.tradingAccounts.groupStrategy : t.tradingAccounts.accountStrategy}</Label>
              <div className="flex flex-wrap gap-2 min-h-[28px]">
                {allStrategies.length === 0 && (
                  <span className="text-xs text-muted-foreground">{t.tradingAccounts.noStrategies}</span>
                )}
                {allStrategies.map((s: any) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setNewGroupStrategyId(newGroupStrategyId === s.id ? null : s.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                      newGroupStrategyId === s.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted text-muted-foreground border-border hover:border-primary/50"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder={t.tradingAccounts.strategyNamePlaceholder}
                  value={newGroupStrategyName}
                  onChange={(e) => setNewGroupStrategyName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddGroupStrategy(); } }}
                  className="h-8 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddGroupStrategy}
                  disabled={newGroupStrategyLoading || !newGroupStrategyName.trim()}
                  className="shrink-0 h-8"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {t.tradingAccounts.addStrategy}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {groupOption === "new" ? t.tradingAccounts.groupStrategyHint : t.tradingAccounts.accountStrategyHint}
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={step === 1 ? resetAndClose : () => { setStep((s) => s - 1); setMtConnectionError(null); }}>
            {step === 1 ? t.tradingAccounts.cancel : t.tradingAccounts.previous}
          </Button>
          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={nextDisabled}
            >
              {t.tradingAccounts.next}
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitDisabled}>
              {loading ? (isMetaTrader ? t.tradingAccounts.mtAccountCreating : "...") : t.tradingAccounts.create}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Account Modal ────────────────────────────────────────────────────────

export function EditAccountModal({
  account,
  groups,
  templates,
  onClose,
  onSuccess,
}: {
  account: EnrichedAccount | null;
  groups: EnrichedGroup[];
  templates: PropFirmTemplateSnapshot[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [initialBalance, setInitialBalance] = useState("");
  const [status, setStatus] = useState<AccountStatus>("challenge");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [tierOverrideId, setTierOverrideId] = useState<string | null>(null);
  const [payoutTarget, setPayoutTarget] = useState("");
  const [challengeCost, setChallengeCost] = useState("");
  const [activationFee, setActivationFee] = useState("");
  const [fundedAt, setFundedAt] = useState("");
  const [purchasedAt, setPurchasedAt] = useState("");
  const [groupId, setGroupId] = useState<string>("none");
  // When groupId === "__new__", the user is creating a group inline; its name
  // lives here and the group is created on save.
  const [newGroupName, setNewGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [disabledAt, setDisabledAt] = useState<string>("");
  const [consistencyRulePercent, setConsistencyRulePercent] = useState("");
  const [maxDailyLossPercent, setMaxDailyLossPercent] = useState("");
  const [selectedStrategyIds, setSelectedStrategyIds] = useState<string[]>([]);
  const [newStrategyName, setNewStrategyName] = useState("");
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  // Challenge d'origine : undefined = non modifié dans la modale.
  const [predecessorId, setPredecessorId] = useState<string | null | undefined>(undefined);

  const { data: strategiesData, refetch: refetchStrategies } = useQuery(getTradingStrategiesByUser);
  const allStrategies = (strategiesData as any[]) ?? [];

  useEffect(() => {
    if (account) {
      setName(account.name);
      setInitialBalance(String(account.initialBalance));
      setStatus(account.status);
      setTemplateId(account.propFirmTemplateId ?? null);
      setTierOverrideId(account.propFirmTierOverrideId ?? null);
      setPayoutTarget(account.payoutTarget ? String(account.payoutTarget) : "");
      setChallengeCost(account.challengeCost ? String(account.challengeCost) : "");
      setActivationFee(account.activationFee != null ? String(account.activationFee) : "");
      // Heure LOCALE et non toISOString() : le champ est un datetime-local, qui
      // affiche et relit en local. Avec de l'UTC, un passage funded saisi à
      // 18:43 se réaffichait "16:43", classait en Challenge des trades pourtant
      // postérieurs, et reculait de 2 h de plus à chaque sauvegarde.
      const rawFundedAt = (account as any).fundedAt;
      setFundedAt(rawFundedAt ? toDateTimeInputValue(rawFundedAt) : "");
      const rawPurchasedAt = (account as any).purchasedAt;
      setPurchasedAt(rawPurchasedAt ? toDateInputValue(rawPurchasedAt) : "");
      setGroupId(account.groupId ?? "none");
      setNewGroupName("");
      const raw = (account as any).disabledAt;
      const alreadyDeactivated = (account as any).isActive === false || account.status === "inactive";
      // Même correction de fuseau : en soirée, toISOString() faisait reculer la
      // date affichée d'un jour pour tout utilisateur à l'est de Greenwich.
      setDisabledAt(raw ? toDateInputValue(raw) : alreadyDeactivated ? toDateInputValue(new Date()) : "");
      setConsistencyRulePercent((account as any).consistencyRulePercent != null ? String((account as any).consistencyRulePercent) : "");
      setMaxDailyLossPercent((account as any).maxDailyLossPercent != null ? String((account as any).maxDailyLossPercent) : "");
      setSelectedStrategyIds((account.strategies ?? []).map((s) => s.id));
      setPredecessorId(undefined);
    }
  }, [account?.id]);

  const handleAddStrategy = async () => {
    const trimmed = newStrategyName.trim();
    if (!trimmed) return;
    setStrategyLoading(true);
    try {
      const created = await createTradingStrategy({ name: trimmed, color: "#71717a" }) as any;
      await refetchStrategies();
      setSelectedStrategyIds((prev) => [...prev, created.id]);
      setNewStrategyName("");
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("existe déjà") || msg.includes("already exists")) {
        toast({ title: t.tradingAccounts.strategyExists, variant: "destructive" });
      } else {
        toast({ title: t.tradingAccounts.strategyCreateError, description: msg, variant: "destructive" });
      }
    } finally {
      setStrategyLoading(false);
    }
  };

  const handleRemoveStrategy = async (id: string) => {
    try {
      await deleteTradingStrategy({ id });
      await refetchStrategies();
      setSelectedStrategyIds((prev) => prev.filter((sid) => sid !== id));
    } catch (err: any) {
      toast({ title: t.tradingAccounts.strategyDeleteError, description: err?.message, variant: "destructive" });
    }
  };

  const toggleStrategy = (id: string) => {
    setSelectedStrategyIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleResyncStrategies = async () => {
    if (!account) return;
    setResyncing(true);
    try {
      const res = await resyncTradesStrategies({ accountId: account.id }) as { updated: number };
      toast({
        title: t.tradingAccounts.resyncTradesStrategiesSuccess,
        description: `${res.updated} trade(s)`,
      });
    } catch (err: any) {
      toast({
        title: t.tradingAccounts.resyncTradesStrategiesError,
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setResyncing(false);
    }
  };

  const { data: chargesData, refetch: refetchCharges } = useQuery(
    getAccountChargesByUser,
    undefined,
    { enabled: !!account }
  );
  const accountCharges = ((chargesData as any[]) ?? []).filter(
    (c: any) => c.accountId === account?.id
  );

  if (!account) return null;

  const isDeactivated = status === "inactive" || (account as any).isActive === false;
  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null;
  const balance = parseFloat(initialBalance) || 0;

  // Modèle du type de compte pour ce formulaire. Tant que rien n'a changé
  // (même firme/palier/solde que le compte en base), lit accountPricingModel
  // (le point d'entrée serveur, qui fait autorité sur le snapshot figé) ;
  // sinon résout à chaud en ANCRANT sur `account.propFirmAccountTypeId`, comme
  // le fait le serveur dans updateAccount — jamais avec un accountTypeId à
  // `null`, qui retombe sur le PREMIER type actif du template (bug revue Task
  // 14 : un compte Lightning direct-funded sur un template à plusieurs types
  // se voyait résolu comme le premier type, souvent one_time).
  const editPricingModel = editFormPricingModel(account, {
    template: selectedTemplate,
    tierOverrideId,
    balance,
  });
  const editDirectFunded = isDirectFunded(editPricingModel);

  const handleSave = async () => {
    if (!name.trim()) return;
    setNameError("");
    setLoading(true);
    try {
      // Inline group creation: create the group first, then assign the account
      // to it. On failure, abort the save so the account isn't left orphaned.
      let effectiveGroupId: string | null = groupId === "none" || groupId === "__new__" ? null : groupId;
      if (groupId === "__new__") {
        const trimmed = newGroupName.trim();
        if (!trimmed) {
          toast({ title: t.tradingAccounts.groupNameRequired, variant: "destructive" });
          setLoading(false);
          return;
        }
        const created = (await createAccountGroup({ name: trimmed })) as { id: string };
        effectiveGroupId = created.id;
      }

      await updateAccount({
        id: account.id,
        name: name.trim(),
        initialBalance: balance,
        currency: CURRENCY,
        status,
        propFirmTemplateId: templateId,
        propFirmTierOverrideId: tierOverrideId,
        payoutTarget: normalizePayoutTarget(payoutTarget),
        challengeCost: challengeCost ? parseFloat(challengeCost) : null,
        challengeCostCurrency: null,
        activationFee: activationFee !== "" ? parseFloat(activationFee) : null,
        activationFeeCurrency: null,
        ...(status === "pa" ? { fundedAt: fundedAt ? new Date(fundedAt).toISOString() : null } : {}),
        // Date d'achat réelle : vide = retour au fallback sur createdAt.
        purchasedAt: purchasedAt ? new Date(purchasedAt).toISOString() : null,
        groupId: effectiveGroupId,
        consistencyRulePercent: consistencyRulePercent !== "" ? parseFloat(consistencyRulePercent) : null,
        maxDailyLossPercent: maxDailyLossPercent !== "" ? parseFloat(maxDailyLossPercent) : null,
        // Intentional: if account is not deactivated, clear disabledAt (reactivation clears crash date)
        disabledAt: isDeactivated && disabledAt ? new Date(disabledAt).toISOString() : null,
        strategyIds: selectedStrategyIds,
      } as any);
      // Après updateAccount : le serveur juge l'éligibilité sur le statut ENREGISTRÉ.
      if (predecessorId !== undefined && templateId && status !== "challenge") {
        await setFundedPredecessor({ accountId: account.id, previousAccountId: predecessorId });
      }
      toast({ title: t.tradingAccounts.accountUpdated, description: t.tradingAccounts.accountUpdatedDescription });
      // Close before refetching — same removeChild race as the add dialog.
      onClose();
      setTimeout(() => onSuccess(), 0);
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("existe déjà") || msg.includes("already exists")) {
        setNameError(t.tradingAccounts.duplicateAccountName);
      } else {
        toast({ title: t.tradingAccounts.error, description: msg || t.tradingAccounts.updateError, variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!account} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.editAccountTitle}</DialogTitle>
          <DialogDescription>{t.tradingAccounts.editAccountDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t.tradingAccounts.accountName}</Label>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(""); }}
              className={nameError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.initialBalanceLabel}</Label>
            <Input type="number" value={initialBalance} onChange={(e) => setInitialBalance(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.propFirm}</Label>
            <PropFirmSelector
              value={templateId}
              onChange={(v) => {
                setTemplateId(v);
                setTierOverrideId(null);
                if (!v && (status === "challenge" || status === "pa")) setStatus("live");
                // Rattacher une prop firm à un compte perso le fait démarrer en
                // challenge. Changer de prop firm sur un compte DÉJÀ prop firm
                // garde son statut : un programme live ("live") n'est pas un
                // compte perso et ne doit pas retomber en challenge.
                if (v && !templateId && (status === "live" || status === "demo")) setStatus("challenge");
              }}
              templates={templates}
            />
          </div>
          {selectedTemplate && (
            <div className="space-y-2">
              <Label>{t.tradingAccounts.selectTier}</Label>
              <TierSelector
                template={selectedTemplate}
                value={tierOverrideId}
                onChange={setTierOverrideId}
                initialBalance={balance}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label>{t.tradingAccounts.accountStatus}</Label>
            <Select value={status} onValueChange={(v) => {
              const newStatus = v as AccountStatus;
              setStatus(newStatus);
              // Auto-fill today's date when switching to inactive if not already set
              const willBeDeactivated = newStatus === "inactive" || (account as any).isActive === false;
              if (willBeDeactivated && !disabledAt) {
                setDisabledAt(toDateInputValue(new Date()));
              }
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {templateId ? (
                  <>
                    {/* Direct funded n'a pas de phase de challenge (refusé par le
                        serveur) : on masque l'option, sauf si le compte est
                        DÉJÀ en challenge en base (bascule en direct funded après
                        coup, à corriger explicitement plutôt qu'à faire
                        disparaître en silence). Le statut STOCKÉ fait foi ici,
                        pas `status` (l'état local du Select) : sinon, sélectionner
                        "challenge" le rendrait indéfiniment visible même après
                        avoir changé d'avis vers un autre statut. */}
                    {(!editDirectFunded || account.status === "challenge") && (
                      <SelectItem value="challenge">{t.tradingAccounts.statusChallenge}</SelectItem>
                    )}
                    <SelectItem value="pa">{t.tradingAccounts.statusPa}</SelectItem>
                    {/* Programme live de la prop firm : phase `live` du palier. */}
                    <SelectItem value="live">{t.tradingAccounts.statusLive}</SelectItem>
                    <SelectItem value="inactive">{t.tradingAccounts.statusInactive}</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="live">{t.tradingAccounts.statusLive}</SelectItem>
                    <SelectItem value="demo">{t.tradingAccounts.statusDemo}</SelectItem>
                    <SelectItem value="inactive">{t.tradingAccounts.statusInactive}</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          {isDeactivated && (
            <div className="space-y-2">
              <Label>{t.tradingAccounts.disabledAtLabel}</Label>
              <Input
                type="date"
                value={disabledAt}
                onChange={(e) => setDisabledAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.disabledAtHint}</p>
            </div>
          )}
          {status === "pa" && (
            <div className="space-y-2">
              <Label>{t.tradingAccounts.payoutTargetLabel}</Label>
              <Input
                type="number"
                placeholder="ex: 5000"
                value={payoutTarget}
                onChange={(e) => setPayoutTarget(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.setPayoutTarget}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>{t.tradingAccounts.challengeCostLabel}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground pointer-events-none select-none">USD</span>
              <Input
                type="number"
                placeholder={t.tradingAccounts.challengeCostPlaceholder}
                value={challengeCost}
                onChange={(e) => setChallengeCost(e.target.value)}
                min={0}
                step={0.01}
                className="pl-12"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.challengeCostHint}</p>
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.purchasedAtLabel}</Label>
            <Input
              type="date"
              value={purchasedAt}
              onChange={(e) => setPurchasedAt(e.target.value)}
              max={toDateInputValue(new Date())}
            />
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.purchasedAtHint}</p>
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.activationFeeLabel}</Label>
            <Input
              type="number"
              placeholder={t.tradingAccounts.activationFeePlaceholder}
              value={activationFee}
              onChange={(e) => setActivationFee(e.target.value)}
              min={0}
              step={0.01}
            />
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.activationFeeHint}</p>
          </div>
          {status === "pa" && (
            <div className="space-y-2">
              <Label>{t.tradingAccounts.fundedAtLabel}</Label>
              <Input
                type="datetime-local"
                value={fundedAt}
                onChange={(e) => setFundedAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.fundedAtHint}</p>
            </div>
          )}
          {/* Compte perso (sans prop firm) : pas de challenge d'origine. */}
          {templateId && status !== "challenge" && (
            <PredecessorSelectField accountId={account.id} value={predecessorId} onChange={setPredecessorId} />
          )}
          <div className="space-y-2">
            <Label>{t.tradingAccounts.strategies}</Label>
            {/* Existing strategy pills */}
            <div className="flex flex-wrap gap-2 min-h-[28px]">
              {allStrategies.length === 0 && (
                <span className="text-xs text-muted-foreground">{t.tradingAccounts.noStrategies}</span>
              )}
              {allStrategies.map((s: any) => {
                const selected = selectedStrategyIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleStrategy(s.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                      selected
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-muted text-muted-foreground border-border hover:border-primary/50"
                    }`}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    {s.name}
                    {selected && (
                      <span
                        role="button"
                        className="ml-0.5 opacity-60 hover:opacity-100 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); toggleStrategy(s.id); }}
                      >
                        ×
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Create new strategy inline */}
            <div className="flex gap-2">
              <Input
                placeholder={t.tradingAccounts.strategyNamePlaceholder}
                value={newStrategyName}
                onChange={(e) => setNewStrategyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddStrategy(); } }}
                className="h-8 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddStrategy}
                disabled={strategyLoading || !newStrategyName.trim()}
                className="shrink-0 h-8"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {t.tradingAccounts.addStrategy}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.strategyHint}</p>
            <div className="flex flex-col gap-1 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResyncStrategies}
                disabled={resyncing}
                className="self-start h-8"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${resyncing ? "animate-spin" : ""}`} />
                {t.tradingAccounts.resyncTradesStrategies}
              </Button>
              <p className="text-xs text-muted-foreground">{t.tradingAccounts.resyncTradesStrategiesHint}</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.groups}</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t.tradingAccounts.ungrouped}</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
                <SelectItem value="__new__">{t.tradingAccounts.createNewGroup}</SelectItem>
              </SelectContent>
            </Select>
            {groupId === "__new__" && (
              <Input
                value={newGroupName}
                placeholder={t.tradingAccounts.groupNamePlaceholder}
                onChange={(e) => setNewGroupName(e.target.value)}
                autoFocus
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.charges}</Label>
            <AccountChargesManager
              charges={accountCharges}
              accountId={account.id}
              onMutated={() => refetchCharges()}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.tradingAccounts.cancel}</Button>
          <Button onClick={handleSave} disabled={loading || !name.trim()}>
            {loading ? "..." : t.tradingAccounts.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Group Modal ─────────────────────────────────────────────────────────

function EditGroupModal({
  group,
  templates,
  onClose,
  onSuccess,
}: {
  group: EnrichedGroup | null;
  templates: PropFirmTemplateSnapshot[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { data: strategiesData } = useQuery(getTradingStrategiesByUser);
  const allStrategies = (strategiesData as any[]) ?? [];

  useEffect(() => {
    if (group) {
      setName(group.name);
      setTemplateId(group.propFirmTemplateId ?? null);
      setStrategyId(group.strategyId ?? null);
    }
  }, [group?.id]);

  if (!group) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await updateAccountGroup({
        id: group.id,
        name: name.trim(),
        propFirmTemplateId: templateId,
        strategyId: strategyId,
      });
      toast({ title: t.tradingAccounts.groupUpdated });
      // Close before refetching — same removeChild race as the add dialog.
      onClose();
      setTimeout(() => onSuccess(), 0);
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!group} onOpenChange={(v) => !v && onClose()}>
      <DialogContent key={group.id}>
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.editGroup}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t.tradingAccounts.groupName}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.tradingAccounts.groupNamePlaceholder} />
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.groupPropFirm}</Label>
            <PropFirmSelector value={templateId} onChange={setTemplateId} templates={templates} />
            <p className="text-xs text-muted-foreground">
              Si définie, les comptes du groupe sans prop firm individuelle hériteront de ces règles.
            </p>
          </div>
          <div className="space-y-2">
            <Label>{t.tradingAccounts.groupStrategy}</Label>
            <Select value={strategyId ?? "none"} onValueChange={(v) => setStrategyId(v === "none" ? null : v)}>
              <SelectTrigger><SelectValue placeholder={t.tradingAccounts.strategiesPlaceholder} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t.tradingAccounts.strategyNone}</SelectItem>
                {allStrategies.map((s: any) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t.tradingAccounts.groupStrategyHint}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.tradingAccounts.cancel}</Button>
          <Button onClick={handleSave} disabled={loading || !name.trim()}>
            {loading ? "..." : t.tradingAccounts.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TradingAccountsPage() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [showPlanModal, setShowPlanModal] = useState(false);

  const isFreePlan = useMemo(() => {
    if (!user) return false;
    if (!user.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free) return true;
    if (!user.subscriptionStatus || user.subscriptionStatus === SubscriptionStatus.Deleted) return true;
    return false;
  }, [user]);

  const isPro = !isFreePlan;

  const canUseMetaApi = useMemo(() => {
    if (!user) return false;
    // Plan EFFECTIF : couvre Max et Max Annuel, l'abonnement supprimé et la
    // grâce d'impayé expirée (même règle que le serveur).
    return userHasBrokerSync(user as any);
  }, [user]);

  const [connectingAccountId, setConnectingAccountId] = useState<string | null>(null);
  const [isTradovateWizardOpen, setIsTradovateWizardOpen] = useState(false);
  // "Add account to existing Tradovate connection" flow (no new OAuth).
  // When set, opens the "add account to an EXISTING connection" wizard scoped
  // to this connection id (one connection per prop firm). Replaces the old
  // single-connection boolean.
  const [addAccountConnectionId, setAddAccountConnectionId] = useState<string | null>(null);

  const handleConnectMetaTrader = (accountId: string) => {
    if (!canUseMetaApi) {
      setShowPlanModal(true);
      return;
    }
    setConnectingAccountId(accountId);
  };

  const { data: accountsData, refetch: refetchAccounts } = useQuery(getAccountsByUser);
  const accounts: EnrichedAccount[] = (accountsData as any)?.accounts ?? [];
  const hiddenAccountsCount: number = (accountsData as any)?.hiddenAccountsCount ?? 0;
  // Un utilisateur peut avoir N connexions Tradovate (une par prop firm).
  // L'argument `{}` doit être identique à celui de TradovateConnectionsSection
  // et TradovateSyncStatus : react-query indexe le cache sur les arguments, donc
  // `undefined` et `{}` créeraient deux entrées distinctes et le refetch d'un
  // composant laisserait les autres avec des données périmées.
  const { data: tradovateConnections = [] } = useQuery(getTradovateConnections, {});
  const { data: groups = [], refetch: refetchGroups } = useQuery(getAccountGroupsByUser);
  const { data: templates = [] } = useQuery(getPropFirmTemplates);
  const { data: pageStrategiesData = [] } = useQuery(getTradingStrategiesByUser);

  const [searchParams, setSearchParams] = useSearchParams();
  const tradovateImportConnectionId = searchParams.get("tradovateImport");
  // `get` rend "" (et non null) quand le param est présent mais vide, ce qui
  // arrive avec les URLs de callback émises avant le fix. Cette chaîne vide
  // finissait dans Account.create et faisait échouer tout l'import.
  const tradovateImportPropFirmId = normalizePropFirmTemplateId(
    searchParams.get("propFirm"),
  );
  const closeTradovateImport = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("tradovateImport");
    next.delete("propFirm");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Open the import review on the *existing* connection (add-account flow) by
  // reusing the same URL-param path the OAuth callback uses. No new OAuth.
  const openTradovateImportForExisting = useCallback(
    (connId: string | null, propFirmTemplateId: string | null) => {
      if (!connId) return;
      const next = new URLSearchParams(searchParams);
      next.set("tradovateImport", connId);
      if (propFirmTemplateId) next.set("propFirm", propFirmTemplateId);
      else next.delete("propFirm");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EnrichedAccount | null>(null);

  // Auto-open edit modal when ?edit=<id> query param is present
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && (accounts as EnrichedAccount[]).length > 0) {
      const account = (accounts as EnrichedAccount[]).find((a) => a.id === editId);
      if (account) {
        setEditingAccount(account);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, accounts]);

  // Surface OAuth failures redirected by the Tradovate callback
  // (?tradovateError=missing_params|invalid_state|exchange_failed|unknown).
  // All four map to the same user action — retry — so one generic message.
  // `plan_required` is the exception: retrying cannot help, the plan must.
  useEffect(() => {
    const oauthError = searchParams.get("tradovateError");
    if (!oauthError) return;
    toast({
      title: t.tradovate.oauthErrorTitle,
      description: oauthError === "plan_required"
        ? t.tradovate.oauthPlanRequiredDescription
        : t.tradovate.oauthErrorDescription,
      variant: "destructive",
    });
    const next = new URLSearchParams(searchParams);
    next.delete("tradovateError");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast, t]);
  const [editingGroup, setEditingGroup] = useState<EnrichedGroup | null>(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState<EnrichedAccount | null>(null);
  const [deactivateAccountTarget, setDeactivateAccountTarget] = useState<EnrichedAccount | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<EnrichedGroup | null>(null);
  const [fundedTransitionTarget, setFundedTransitionTarget] = useState<EnrichedAccount | null>(null);

  const refetchAll = useCallback(() => {
    refetchAccounts();
    refetchGroups();
  }, [refetchAccounts, refetchGroups]);

  const [hideInactive, setHideInactive] = useState<boolean>(() => {
    return localStorage.getItem('accounts.hideInactive') === 'true';
  });
  const [accountsView, setAccountsView] = useLocalStorage<'cards' | 'table'>('edgely:accounts-view', 'cards');
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const pageStrategies = (pageStrategiesData as any[]);

  const ungroupedAccounts = useMemo(
    () => (accounts as EnrichedAccount[]).filter((a) => {
      if (a.groupId) return false;
      if (hideInactive && !a.isActive) return false;
      if (strategyIds.length > 0 && !strategyIds.some((id) => (a as any).strategies?.some((s: any) => s.id === id))) return false;
      return true;
    }),
    [accounts, hideInactive, strategyIds],
  );

  // Les deux requêtes de la page ne rendent PAS la même forme de compte :
  // getAccountsByUser passe par buildEnrichedAccounts (bufferStatus,
  // challengeProgress, payoutProgress, activeTier, effectiveTemplate...), alors
  // que getAccountGroupsByUser renvoie des lignes Prisma à peine dérivées
  // (currentBalance, ruleViolations, tradesCount seulement). Les cards affichées
  // DANS un groupe lisaient donc des champs `undefined` et ne pouvaient pas
  // dessiner leur axe de progression, là où la page de détail du même compte
  // l'affichait. On substitue ici chaque compte de groupe par son homologue
  // enrichi, indexé une seule fois (O(n), pas un find par compte).
  //
  // Un compte présent dans un groupe mais ABSENT de la liste enrichie est
  // ÉCARTÉ, jamais laissé passer brut : getAccountsByUser filtre par
  // getVisibleAccountIds (en Free, un seul compte visible) alors que
  // getAccountGroupsByUser ne filtre pas du tout. Le laisser passer exposerait
  // un compte que la limite de plan masque.
  const enrichedAccountsById = useMemo(() => {
    const map = new Map<string, EnrichedAccount>();
    for (const account of accounts as EnrichedAccount[]) map.set(account.id, account);
    return map;
  }, [accounts]);

  const sortedGroups = useMemo(() => {
    const gs = groups as EnrichedGroup[];
    const filtered = gs.map((g) => ({
      ...g,
      accounts: g.accounts.flatMap((rawAcc) => {
        const acc = enrichedAccountsById.get(rawAcc.id);
        if (!acc) return [];
        if (hideInactive && !acc.isActive) return [];
        if (strategyIds.length > 0 && !strategyIds.some((id) => (acc as any).strategies?.some((s: any) => s.id === id))) return [];
        return [acc];
      }),
    })).filter((g) => strategyIds.length === 0 || g.accounts.length > 0);

    if (!hideInactive) {
      return filtered.sort((a, b) => {
        const aAllInactive = a.accounts.length > 0 && a.accounts.every((acc) => !acc.isActive);
        const bAllInactive = b.accounts.length > 0 && b.accounts.every((acc) => !acc.isActive);
        return Number(aAllInactive) - Number(bAllInactive);
      });
    }
    return filtered.filter((g) => g.accounts.length > 0);
  }, [groups, enrichedAccountsById, hideInactive, strategyIds]);

  // Use the flat accounts list from getAccountsByUser (fully enriched: effectiveTemplate, group, etc.)
  // rather than extracting from sortedGroups which lack those fields.
  const allAccountsFlat = useMemo(
    () => (accounts as EnrichedAccount[]).filter((a) => {
      if (hideInactive && !a.isActive) return false;
      if (strategyIds.length > 0 && !strategyIds.some((id) => (a as any).strategies?.some((s: any) => s.id === id))) return false;
      return true;
    }),
    [accounts, hideInactive, strategyIds],
  );

  const handleToggleActive = async (account: EnrichedAccount) => {
    try {
      const isDeactivating = account.isActive;
      const existingDisabledAt = (account as any).disabledAt;
      await updateAccount({
        id: account.id,
        isActive: !account.isActive,
        // Set disabledAt to today when deactivating (if not already set), clear it when reactivating
        disabledAt: isDeactivating && !existingDisabledAt ? new Date().toISOString() : isDeactivating ? undefined : null,
      });
      toast({
        title: account.isActive ? t.tradingAccounts.accountDeactivated : t.tradingAccounts.accountActivated,
      });
      refetchAll();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateAccountTarget) return;
    try {
      const existingDisabledAt = (deactivateAccountTarget as any).disabledAt;
      await updateAccount({
        id: deactivateAccountTarget.id,
        isActive: false,
        disabledAt: existingDisabledAt ? undefined : new Date().toISOString(),
      });
      toast({ title: t.tradingAccounts.accountDeactivated });
      refetchAll();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setDeactivateAccountTarget(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountTarget) return;
    try {
      await deleteAccount({ id: deleteAccountTarget.id });
      toast({ title: t.tradingAccounts.accountDeleted, description: t.tradingAccounts.accountDeletedDescription });
      refetchAll();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message || t.tradingAccounts.deleteError, variant: "destructive" });
    } finally {
      setDeleteAccountTarget(null);
    }
  };

  // From the delete dialog: switch to deactivation instead
  const handleDeactivateInstead = () => {
    if (!deleteAccountTarget) return;
    setDeactivateAccountTarget(deleteAccountTarget);
    setDeleteAccountTarget(null);
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupTarget) return;
    try {
      await deleteAccountGroup({ id: deleteGroupTarget.id });
      toast({ title: t.tradingAccounts.groupDeleted });
      refetchAll();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setDeleteGroupTarget(null);
    }
  };

  const totalAccounts = (accounts as EnrichedAccount[]).length;
  const totalActiveAccounts = (accounts as EnrichedAccount[]).filter((a) => a.isActive).length;

  return (
    <PageTransition className="w-full py-8 px-4">
      {/* Page header */}
      <div className="mb-6 space-y-3">
        <RuleUpdateBanner />
        {/* Comptes prop firm sans aucune règle surveillée. La bannière se filtre
            elle-même sur `hasNoRules` et ne rend rien quand tout est configuré.
            Branchée sur l'édition de compte : c'est là que se choisit la prop
            firm, donc le geste qui comble l'angle mort. */}
        <NoRulesBanner
          accounts={accounts as EnrichedAccount[]}
          onConfigure={(accountId) => {
            const target = (accounts as EnrichedAccount[]).find((a) => a.id === accountId);
            if (target) setEditingAccount(target);
          }}
        />
      </div>

      {/* ── L'ENTETE PASSE A LA LIGNE, ELLE NE SE COMPRIME PAS ───────────────
          Sans `flex-wrap`, la barre d'actions (6 controles, dont un bouton au
          libelle long) gardait sa largeur intrinseque et volait toute la place
          au titre : en navigateur reduit, « Comptes de Trading » se repliait
          mot par mot sur trois lignes pendant que la barre debordait quand
          meme du cadre. `min-w-0` sur le bloc titre l'autorise a retrecir
          avant de forcer le retour a la ligne, et la barre passe dessous des
          qu'elle ne tient plus. */}
      <motion.div
        className="flex flex-wrap items-start justify-between gap-3 mb-6"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <div className="min-w-0">
          <h1 className="text-2xl font-bold">{t.tradingAccounts.title}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {totalActiveAccounts}/{totalAccounts} comptes actifs · {t.tradingAccounts.activeAccountsOnly}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <EdgyHeaderButton />
          <div className="flex items-center rounded-md border border-border overflow-hidden">
            <button
              type="button"
              title={t.tradingAccounts.cardsView}
              onClick={() => setAccountsView('cards')}
              className={`p-2 transition-colors ${
                accountsView === 'cards'
                  ? 'bg-accent/50 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/30'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              title={t.tradingAccounts.tableView}
              onClick={() => setAccountsView('table')}
              className={`p-2 transition-colors ${
                accountsView === 'table'
                  ? 'bg-accent/50 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/30'
              }`}
            >
              <Table2 className="h-4 w-4" />
            </button>
          </div>
          {pageStrategies.length > 0 && (
            <StrategyMultiSelect
              strategies={pageStrategies}
              selectedIds={strategyIds}
              onChange={setStrategyIds}
              className="min-w-[180px] text-sm"
            />
          )}
          <Button
            variant="outline"
            size="default"
            onClick={() => setHideInactive((v) => {
              const next = !v;
              localStorage.setItem('accounts.hideInactive', String(next));
              return next;
            })}
          >
            {hideInactive ? t.tradingAccounts.showInactiveAccounts : t.tradingAccounts.hideInactiveAccounts}
          </Button>
          <BeamButton size="default" onClick={() => {
            if (isFreePlan && totalAccounts >= 1) {
              setShowPlanModal(true);
            } else {
              setIsAddModalOpen(true);
            }
          }}>
            <Plus className="h-4 w-4 mr-2" />
            {t.tradingAccounts.addAccounts}
          </BeamButton>
        </div>
      </motion.div>

      {/* Tradovate connections — one card per prop firm. See
          TradovateConnectionsSection for the section-level "connect another
          prop firm" action vs. the per-card "add an account to this firm".
          `renderAccounts` réinjecte AccountTable pour que les lignes dépliées
          soient exactement celles du tableau du dessous (même rendu, mêmes
          actions) au lieu d'une copie divergente. */}
      <TradovateConnectionsSection
        view={accountsView}
        onConnectNewPropFirm={() => setIsTradovateWizardOpen(true)}
        onAddAccountToConnection={(connectionId) => {
          // La connexion porte déjà sa prop firm : redemander « Choisis la prop
          // firm » n'apporte rien et permet même de choisir la mauvaise. On
          // n'ouvre le wizard que pour une connexion non rattachée.
          const conn = (tradovateConnections as any[]).find((c: any) => c.id === connectionId);
          const propFirmId = conn?.propFirmTemplate?.id ?? null;
          if (propFirmId) {
            openTradovateImportForExisting(connectionId, propFirmId);
            return;
          }
          setAddAccountConnectionId(connectionId);
        }}
        accounts={allAccountsFlat}
        renderAccounts={(accountsForConnection) => (
          <AccountTable
            accounts={accountsForConnection}
            groups={groups}
            onEdit={setEditingAccount}
            onDelete={setDeleteAccountTarget}
            onToggleActive={handleToggleActive}
            onRequestDeactivate={setDeactivateAccountTarget}
            onOpenDetail={(a) => navigate(`/account/${a.id}`)}
            onFundedTransition={setFundedTransitionTarget}
            isPro={isPro}
            onConnect={handleConnectMetaTrader}
          />
        )}
      />

      {/* Nouveaux comptes côté broker : composant partagé avec le Lite. */}
      <TradovateDiscoveredBanners onImport={openTradovateImportForExisting} />

      {hiddenAccountsCount > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-4 py-3 text-sm text-[hsl(var(--warning))]">
          <span>
            {t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-4 shrink-0 border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/20"
            onClick={() => setShowPlanModal(true)}
          >
            {t.tradingAccounts.hiddenBanner.cta}
          </Button>
        </div>
      )}

      {accountsView === 'table' ? (
        allAccountsFlat.length > 0 ? (
          <AccountTable
            accounts={allAccountsFlat}
            groups={groups}
            onEdit={setEditingAccount}
            onDelete={setDeleteAccountTarget}
            onToggleActive={handleToggleActive}
            onRequestDeactivate={setDeactivateAccountTarget}
            onOpenDetail={(a) => navigate(`/account/${a.id}`)}
            onFundedTransition={setFundedTransitionTarget}
            isPro={isPro}
            onConnect={handleConnectMetaTrader}
          />
        ) : totalAccounts > 0 ? (
          // DES COMPTES EXISTENT, le filtre les masque tous. Afficher ici
          // « Aucun compte créé » était faux et sans issue : un trader dont tous
          // les comptes viennent d'être cramés voyait sa page se vider et se
          // faire proposer d'en créer un, sans rien qui indique que ses comptes
          // étaient toujours là. Le filtre est de surcroît persisté en
          // localStorage, donc l'état survivait au rechargement.
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Folder className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">{t.tradingAccounts.allAccountsInactive}</p>
            {/* Persiste le choix, comme le bouton de la barre d'outils : sans
                l'écriture localStorage, les comptes réapparaissent puis se
                remasquent au rechargement suivant. */}
            <Button
              variant="outline"
              size="default"
              onClick={() => {
                localStorage.setItem("accounts.hideInactive", "false");
                setHideInactive(false);
              }}
            >
              {t.tradingAccounts.showInactiveAccounts}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Folder className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">{t.tradingAccounts.noAccounts}</p>
            <BeamButton size="default" onClick={() => setIsAddModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t.tradingAccounts.addAccounts}
            </BeamButton>
          </div>
        )
      ) : (
        <>
          {/* Groups */}
          {sortedGroups.length > 0 && (
            <div className="space-y-4 mb-6">
              {sortedGroups.map((group) => {
                const allInactive = group.accounts.length > 0 && group.accounts.every((a) => !a.isActive);
                return (
                  <GroupSection
                    key={group.id}
                    group={group}
                    allInactive={allInactive}
                    onEditGroup={setEditingGroup}
                    onDeleteGroup={setDeleteGroupTarget}
                    onEditAccount={setEditingAccount}
                    onDeleteAccount={setDeleteAccountTarget}
                    onToggleActive={handleToggleActive}
                    onRequestDeactivate={setDeactivateAccountTarget}
                    onOpenDetail={(a) => navigate(`/account/${a.id}`)}
                    onFundedTransition={setFundedTransitionTarget}
                    isPro={isPro}
                    onConnect={handleConnectMetaTrader}
                  />
                );
              })}
            </div>
          )}

          {/* Ungrouped */}
          {ungroupedAccounts.length > 0 && (
            <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2">
                <Folder className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm">{t.tradingAccounts.ungrouped}</span>
                <span className="text-xs text-muted-foreground">({ungroupedAccounts.length})</span>
              </div>
              <div className="px-4 pb-4 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {ungroupedAccounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    onEdit={setEditingAccount}
                    onDelete={setDeleteAccountTarget}
                    onToggleActive={handleToggleActive}
                    onRequestDeactivate={setDeactivateAccountTarget}
                    onOpenDetail={(a) => navigate(`/account/${a.id}`)}
                    onFundedTransition={setFundedTransitionTarget}
                    isPro={isPro}
                    onConnect={handleConnectMetaTrader}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {totalAccounts === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Folder className="h-12 w-12 text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground mb-4">{t.tradingAccounts.noAccounts}</p>
              <BeamButton size="default" onClick={() => setIsAddModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t.tradingAccounts.addAccounts}
              </BeamButton>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <AddAccountsModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        groups={groups as EnrichedGroup[]}
        templates={templates as PropFirmTemplateSnapshot[]}
        existingAccounts={accounts as { name: string }[]}
        onSuccess={refetchAll}
        canUseMetaApi={canUseMetaApi}
        onUpgradeRequest={() => { setIsAddModalOpen(false); setShowPlanModal(true); }}
        tradovateConnections={tradovateConnections as any[]}
        onAddToConnection={(connId) => { setIsAddModalOpen(false); setAddAccountConnectionId(connId); }}
        onConnectNewPropFirm={() => { setIsAddModalOpen(false); setIsTradovateWizardOpen(true); }}
      />

      <EditAccountModal
        account={editingAccount}
        groups={(groups as EnrichedGroup[]) ?? []}
        templates={templates as PropFirmTemplateSnapshot[]}
        onClose={() => setEditingAccount(null)}
        onSuccess={refetchAll}
      />

      <EditGroupModal
        group={editingGroup}
        templates={templates as PropFirmTemplateSnapshot[]}
        onClose={() => setEditingGroup(null)}
        onSuccess={refetchAll}
      />

      {/* Delete account confirm */}
      <Dialog open={!!deleteAccountTarget} onOpenChange={(v) => !v && setDeleteAccountTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.tradingAccounts.deleteAccountConfirmTitle.replace("{name}", deleteAccountTarget?.name ?? "")}</DialogTitle>
            <DialogDescription>
              {t.tradingAccounts.deleteAccountConfirmDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{t.tradingAccounts.deleteAccountConfirmWarning}</p>
            </div>
            <div className="flex gap-2 rounded-md border border-border bg-muted/40 p-3">
              <Power className="h-4 w-4 shrink-0 text-primary mt-0.5" />
              <p className="text-sm text-muted-foreground">{t.tradingAccounts.deleteAccountConfirmHint}</p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteAccountTarget(null)}>{t.tradingAccounts.cancel}</Button>
            <Button variant="secondary" onClick={handleDeactivateInstead}>
              <Power className="h-4 w-4 mr-2" />
              {t.tradingAccounts.deactivateInstead}
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount}>{t.tradingAccounts.delete}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate account confirm */}
      <Dialog open={!!deactivateAccountTarget} onOpenChange={(v) => !v && setDeactivateAccountTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.tradingAccounts.deactivateAccountConfirmTitle.replace("{name}", deactivateAccountTarget?.name ?? "")}</DialogTitle>
            <DialogDescription>
              {t.tradingAccounts.deactivateAccountConfirmDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateAccountTarget(null)}>{t.tradingAccounts.cancel}</Button>
            <Button onClick={handleConfirmDeactivate}>
              <Power className="h-4 w-4 mr-2" />
              {t.tradingAccounts.deactivate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete group confirm */}
      <Dialog open={!!deleteGroupTarget} onOpenChange={(v) => !v && setDeleteGroupTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.tradingAccounts.deleteGroup} "{deleteGroupTarget?.name}" ?</DialogTitle>
            <DialogDescription>{t.tradingAccounts.groupDeleteWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGroupTarget(null)}>{t.tradingAccounts.cancel}</Button>
            <Button variant="destructive" onClick={handleDeleteGroup}>{t.tradingAccounts.deleteGroup}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {fundedTransitionTarget && (
        <PromoteToFundedModal
          open={!!fundedTransitionTarget}
          onOpenChange={(v) => { if (!v) setFundedTransitionTarget(null); }}
          accountId={fundedTransitionTarget.id}
          initialBalance={fundedTransitionTarget.initialBalance}
          trades={[]}
          onPromoted={() => { setFundedTransitionTarget(null); window.location.reload(); }}
        />
      )}

      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />

      {/* MetaAPI Connect Modal */}
      {connectingAccountId && (
        <MetaApiConnectModal
          open={!!connectingAccountId}
          accountId={connectingAccountId}
          onClose={() => setConnectingAccountId(null)}
          onSuccess={() => setConnectingAccountId(null)}
        />
      )}

      {/* Tradovate connect wizard */}
      <TradovateConnectWizard
        open={isTradovateWizardOpen}
        onClose={() => setIsTradovateWizardOpen(false)}
      />

      {/* Tradovate "add account to existing connection" wizard (no new OAuth),
          scoped to the connection whose "+ Add account" was clicked. */}
      {addAccountConnectionId && (
        <TradovateConnectWizard
          open={!!addAccountConnectionId}
          onClose={() => setAddAccountConnectionId(null)}
          existingConnectionId={addAccountConnectionId}
          onAddAccount={(propFirmId) => openTradovateImportForExisting(addAccountConnectionId, propFirmId)}
        />
      )}

      {/* Tradovate import review (mounted from OAuth callback redirect ?tradovateImport=...) */}
      <Dialog
        open={!!tradovateImportConnectionId}
        onOpenChange={(v) => !v && closeTradovateImport()}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {tradovateImportConnectionId && (
            <TradovateImportReview
              connectionId={tradovateImportConnectionId}
              propFirmTemplateId={tradovateImportPropFirmId}
              onDone={() => {
                closeTradovateImport();
                refetchAll();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
}
